import React, { useMemo, useState, useCallback, useRef, useEffect, useLayoutEffect, type ReactNode, isValidElement, cloneElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { TallRowContext } from '@/lib/tallRowContext';
import type { WidgetConfig } from '@/components/dashboard/types';
import type { WidgetCustomizationState } from '@/lib/dashboardTypes';
import { gregorianToDay13 } from '@/lib/calendar13';
import { MONTH_NAMES, WEEKDAY_NAMES, PLASMA_NAMES } from '@/lib/calendar13Types';
import { CYCLE_GROUP_PRESETS, PRESET_CYCLES } from '@/lib/cycleConfig';
import { loadCycleGroups, saveCycleGroup, deleteCycleGroup, subscribeCycleGroups, type CycleGroupData } from '@/lib/cycleGroupStore';
import { format } from 'date-fns';
import type { CycleRing } from '@/lib/cycleTypes';
import { cn } from '@/lib/utils';
import { Heart, Sparkles, RotateCcw, Settings2, Link2, Link2Off, Pencil, Layers, Star, Moon, Sun, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Clock, Plus, Circle, CalendarDays, AudioWaveform, Bell, Orbit, Eclipse } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { MultiRing } from '@/components/calendar/MultiRing';
import { MesaLogo } from '@/components/layout/MesaLogo';

const TOOLBAR_WIDGET_IDS = new Set(['gregorian-grid', 'grid', 'waveform', 'reminders', 'ai-day-summary', 'year-ring']);

interface DreamspellSkinProps {
  cycleWidget: WidgetConfig | null;
  currentDate?: Date;
  cycleRings?: CycleRing[];
  hiddenRings?: string[];
  onToggleRing?: (ringId: string) => void;
  onRingsChange?: (next: CycleRing[]) => void;
  onRotationChange?: (dayOfYear: number, fractionOfDay: number) => void;
  onSyncToNow?: () => void;
  cycleDisplayMode?: 'single' | 'multi';
  onCycleDisplayModeChange?: (mode: 'single' | 'multi') => void;
  navItems?: Array<{ id: string; label: string; icon: ReactNode; to: string }>;
  onOpenTheme?: () => void;
  isAdmin?: boolean;
  viewMode?: 'skin' | 'pager' | 'grid';
  onViewModeChange?: (mode: 'skin' | 'pager' | 'grid') => void;
  selectedCycleWidgetId?: string;
  onSelectCycleWidget?: (widgetId: string) => void;
  widgets?: WidgetConfig[];
  hiddenWidgets?: WidgetConfig[];
  addableWidgets?: WidgetConfig[];
  onAddWidgetById?: (widgetId: string) => void;
  onToggleWidgetVisibility?: (widgetId: string) => void;
  onToggleWidgetSync?: (widgetId: string) => void;
  widgetSyncModes?: Record<string, 'linked' | 'isolated' | 'fixed'>;
  widgetCustomizations?: Record<string, WidgetCustomizationState>;
  onWidgetCustomizationChange?: (widgetId: string, customizationPatch: Partial<WidgetCustomizationState>) => void;
  skinRowOrder?: ('short' | 'medium' | 'tall')[];
  onSkinRowOrderChange?: (order: ('short' | 'medium' | 'tall')[]) => void;
  skinRowFractions?: [number, number, number];
  onSkinRowFractionsChange?: (fractions: [number, number, number]) => void;
  onSkinRowFractionsCommit?: (fractions: [number, number, number]) => void;
  isNowLocked?: boolean;
}

const MOBILE_PAGER_BOTTOM_NAV_HEIGHT_PX = 48;
const DREAMSPELL_LAYOUT_DEFAULTS_KEY = '__dreamspell_layout_defaults__';
const BOTTOM_TAB_LONG_PRESS_MS = 520;
const HOME_WIDGET_ID = '__home__';

const KNOWN_NEW_MOON = new Date('2012-12-13T08:42:00Z').getTime();
const SYNODIC_MONTH = 29.530588853;

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toTimeInputValue = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const toDateTimeLocalInputValue = (date: Date): string => {
  return `${toDateInputValue(date)}T${toTimeInputValue(date)}`;
};

const getMoonPhase = (date: Date): number => {
  const daysSinceNewMoon = (date.getTime() - KNOWN_NEW_MOON) / (1000 * 60 * 60 * 24);
  let phase = (daysSinceNewMoon % SYNODIC_MONTH) / SYNODIC_MONTH;
  if (phase < 0) phase += 1;
  return phase;
};

const getMoonPhaseName = (phase: number): string => {
  if (phase < 0.03 || phase > 0.97) return 'New Moon';
  if (phase < 0.22) return 'Waxing Crescent';
  if (phase < 0.28) return 'First Quarter';
  if (phase < 0.47) return 'Waxing Gibbous';
  if (phase < 0.53) return 'Full Moon';
  if (phase < 0.72) return 'Waning Gibbous';
  if (phase < 0.78) return 'Last Quarter';
  return 'Waning Crescent';
};

// Ornate celestial SVG decorations
const CelestialBorder = ({ className = '' }: { className?: string }) => (
  <svg className={cn("absolute inset-0 w-full h-full pointer-events-none", className)} viewBox="0 0 400 100" preserveAspectRatio="none">
    <defs>
      <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#d4a574" stopOpacity="0.3" />
        <stop offset="50%" stopColor="#f4d03f" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#d4a574" stopOpacity="0.3" />
      </linearGradient>
    </defs>
    <path d="M0,50 Q100,20 200,50 Q300,80 400,50" stroke="url(#goldGradient)" strokeWidth="1" fill="none" />
    <circle cx="200" cy="50" r="4" fill="#f4d03f" opacity="0.5" />
    <circle cx="50" cy="50" r="2" fill="#d4a574" opacity="0.4" />
    <circle cx="350" cy="50" r="2" fill="#d4a574" opacity="0.4" />
  </svg>
);

const MoonPhaseVisual = ({ phase, size = 48 }: { phase: number; size?: number }) => {
  const illumination = phase < 0.5 ? phase * 2 : 2 - phase * 2;
  const isWaxing = phase < 0.5;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Outer glow */}
      <div 
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(244,208,63,0.3) 0%, transparent 70%)',
          transform: 'scale(1.5)',
        }}
      />
      {/* Moon base */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 shadow-inner" />
      {/* Illuminated portion */}
      <div 
        className="absolute inset-0 rounded-full"
        style={{
          background: 'linear-gradient(135deg, #f4d03f 0%, #d4a574 50%, #c9a869 100%)',
          clipPath: isWaxing
            ? `polygon(${50 - (1 - illumination) * 50}% 0, 100% 0, 100% 100%, ${50 - (1 - illumination) * 50}% 100%)`
            : `polygon(0 0, ${50 + (1 - illumination) * 50}% 0, ${50 + (1 - illumination) * 50}% 100%, 0 100%)`,
          boxShadow: 'inset 0 0 20px rgba(244,208,63,0.5)',
        }}
      />
      {/* Highlight */}
      <div 
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 0%, transparent 50%)',
        }}
      />
    </div>
  );
};

// Decorative celestial symbols
const CelestialSymbol = ({ type, className = '' }: { type: 'star' | 'moon' | 'sun' | 'dot'; className?: string }) => {
  const symbols = {
    star: <Star className={cn("text-amber-300/60", className)} />,
    moon: <Moon className={cn("text-amber-200/50", className)} />,
    sun: <Sun className={cn("text-amber-400/40", className)} />,
    dot: <div className={cn("w-1.5 h-1.5 rounded-full bg-amber-300/50", className)} />,
  };
  return symbols[type];
};

// Helper to resolve component values that might be functions
const resolveComponent = (component: React.ReactNode | ((props?: any) => React.ReactNode), props?: any): React.ReactNode => {
  if (typeof component === 'function') {
    return React.createElement(component as React.ComponentType<any>, props ?? {});
  }
  return component;
};

const WIDGET_TAB_META: Record<string, { icon: ReactNode; label: string }> = {
  'gregorian-grid': { icon: <CalendarDays className="h-5 w-5" />,   label: 'Gregorian' },
  'grid':           { icon: <Orbit className="h-5 w-5" />,           label: 'Galactic' },
  'waveform':       { icon: <AudioWaveform className="h-5 w-5" />,   label: 'Waveform' },
  'reminders':      { icon: <Bell className="h-5 w-5" />,            label: 'Reminders' },
  'ai-day-summary': { icon: <Sparkles className="h-5 w-5" />,        label: 'Alignment' },
  'year-ring':      { icon: <Eclipse className="h-5 w-5" />,         label: '13 Moons' },
};

const inferWidgetIcon = (widget?: WidgetConfig): ReactNode => {
  if (!widget) return null;
  const meta = WIDGET_TAB_META[widget.id];
  if (meta) return meta.icon;
  if (widget?.icon && isValidElement(widget.icon)) return widget.icon;
  const token = `${widget?.id ?? ''} ${widget?.title ?? ''}`.toLowerCase();
  if (token.includes('cycle') || token.includes('interlocking') || token.includes('ring')) return <Layers className="h-5 w-5" />;
  if (token.includes('timeline') || token.includes('time')) return <Clock className="h-5 w-5" />;
  if (token.includes('moon')) return <Moon className="h-5 w-5" />;
  if (token.includes('sun') || token.includes('day') || token.includes('gregorian') || token.includes('grid')) return <Sun className="h-5 w-5" />;
  return <span className="text-[11px] font-semibold uppercase">{(widget?.title ?? widget?.id ?? '?').slice(0, 1)}</span>;
};

const STAGE_ICON_PRESETS: Array<{ id: string; name: string; icons: string[] }> = [
  { id: 'moon', name: 'Moon', icons: ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'] },
  { id: 'elements', name: 'Elements', icons: ['🔥', '💧', '🌬️', '🪨'] },
  { id: 'stars', name: 'Stars', icons: ['✦', '✧', '✶', '✺', '✹'] },
];

const getAiWidgetPreset = (widget: WidgetConfig) => {
  const token = `${widget.id} ${widget.title}`.toLowerCase();
  if (token.includes('moon') || token.includes('lunar')) {
    return { icon: '🌙', name: widget.title || 'Lunar Cycles', stageIcons: STAGE_ICON_PRESETS[0].icons };
  }
  if (token.includes('sun') || token.includes('day') || token.includes('gregorian')) {
    return { icon: '☀️', name: widget.title || 'Solar Day', stageIcons: STAGE_ICON_PRESETS[1].icons };
  }
  if (token.includes('weather') || token.includes('wave') || token.includes('space')) {
    return { icon: '🛰️', name: widget.title || 'Signal Wave', stageIcons: STAGE_ICON_PRESETS[2].icons };
  }
  if (token.includes('timeline') || token.includes('time')) {
    return { icon: '⏱️', name: widget.title || 'Timeline', stageIcons: STAGE_ICON_PRESETS[2].icons };
  }
  if (token.includes('cycle') || token.includes('ring')) {
    return { icon: '🌀', name: widget.title || 'Cycle Ring', stageIcons: STAGE_ICON_PRESETS[0].icons };
  }
  return { icon: '✨', name: widget.title || 'Widget', stageIcons: STAGE_ICON_PRESETS[2].icons };
};

export function DreamspellSkin({
  cycleWidget, 
  currentDate = new Date(),
  cycleRings = [],
  hiddenRings = [],
  onToggleRing,
  onRingsChange,
  onRotationChange,
  onSyncToNow,
  cycleDisplayMode = 'multi',
  onCycleDisplayModeChange,
  navItems = [],
  onOpenTheme,
  isAdmin = false,
  viewMode = 'skin',
  onViewModeChange,
  selectedCycleWidgetId,
  onSelectCycleWidget,
  widgets = [],
  hiddenWidgets = [],
  addableWidgets = [],
  onAddWidgetById,
  onToggleWidgetVisibility,
  onToggleWidgetSync,
  widgetSyncModes = {},
  widgetCustomizations = {},
  onWidgetCustomizationChange,
  skinRowOrder: skinRowOrderProp,
  onSkinRowOrderChange,
  skinRowFractions: skinRowFractionsProp,
  onSkinRowFractionsChange,
  onSkinRowFractionsCommit,
  isNowLocked = false,
}: DreamspellSkinProps) {
  const navigate = useNavigate();
  // No exclusions: all widgets eligible for short, medium, or tall
  const shortSliderExcludedWidgetIds = useMemo(() => new Set(), []);
  const [isTallExpanded, setIsTallExpanded] = useState(false);
  const [showLove, setShowLove] = useState(false);
  const [isHomeWidgetActive, setIsHomeWidgetActive] = useState(false);
  const [manualDateInput, setManualDateInput] = useState(() => toDateInputValue(currentDate));
  const [manualTimeInput, setManualTimeInput] = useState(() => toTimeInputValue(currentDate));
  const [lockedLocalDate, setLockedLocalDate] = useState<Date | null>(null);
  const tallWidgetDate = isNowLocked && lockedLocalDate ? lockedLocalDate : currentDate;
  const manualInputFocusedRef = useRef(false);
  const [shortSlideIndex, setShortSlideIndex] = useState(0);
  const [mediumSlideIndex, setMediumSlideIndex] = useState(0);
  const [tallSlideIndex, setTallSlideIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const VALID_ROW_NAMES = ['short', 'medium', 'tall'] as const;
  type RowName = 'short' | 'medium' | 'tall';
  const [showCycleLayoutDialog, setShowCycleLayoutDialog] = useState(false);
  const [showMobileConfigDialog, setShowMobileConfigDialog] = useState(false);
  const [showHideWidgetDialog, setShowHideWidgetDialog] = useState(false);
  const [pendingHideWidgetId, setPendingHideWidgetId] = useState<string | null>(null);
  const [dateStripMode, setDateStripMode] = useState<'cycle28' | 'gregorian'>('cycle28');
  const [mobileConfigWidgetId, setMobileConfigWidgetId] = useState<string>('');
  const [mobileConfigRingId, setMobileConfigRingId] = useState<string>('');
  const [mobileCycleGroups, setMobileCycleGroups] = useState<CycleGroupData[]>([]);
  const [mobileSelectedCycleGroupId, setMobileSelectedCycleGroupId] = useState<string>('');
  const [mobileCycleGroupName, setMobileCycleGroupName] = useState<string>('');
  const [mobileCycleGroupDescription, setMobileCycleGroupDescription] = useState<string>('');
  const [mobileAddRingPresetId, setMobileAddRingPresetId] = useState<string>('earth');
  const [showAddRingPicker, setShowAddRingPicker] = useState(false);
  const [mobileStageIconsInput, setMobileStageIconsInput] = useState<string>('');
  const [mobileStageLabelsDraft, setMobileStageLabelsDraft] = useState<string>('');
  const [defaultWidgetByRow, setDefaultWidgetByRow] = useState<Partial<Record<RowName, string>>>({});
  const defaultAddRequestedRef = useRef<Partial<Record<RowName, string>>>({});
  const normalizedRowOrder: RowName[] = useMemo(() => {
    const provided = Array.isArray(skinRowOrderProp)
      ? skinRowOrderProp.filter((row): row is RowName => VALID_ROW_NAMES.includes(row))
      : [];
    const deduped = Array.from(new Set(provided));
    const missing = VALID_ROW_NAMES.filter((row) => !deduped.includes(row));
    return [...deduped, ...missing];
  }, [skinRowOrderProp]);
  const activeRowOrder: RowName[] = normalizedRowOrder;
  const getGridRow = useCallback((row: RowName) => String(activeRowOrder.indexOf(row) + 1), [activeRowOrder]);
  const moveRow = useCallback((row: RowName, direction: 'up' | 'down') => {
    const idx = activeRowOrder.indexOf(row);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= activeRowOrder.length) return;
    const next = [...activeRowOrder];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    onSkinRowOrderChange?.(next);
  }, [activeRowOrder, onSkinRowOrderChange]);

  const [showInlineWaveform, setShowInlineWaveform] = useState(false);
  const [controlsActive, setControlsActive] = useState(false);
  const [waveformSlideReady, setWaveformSlideReady] = useState(false);
  const [shortRowLoading, setShortRowLoading] = useState(true);
  const [mediumRowLoading, setMediumRowLoading] = useState(true);
  const [tallRowLoading, setTallRowLoading] = useState(true);
  const shortRowLoadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mediumRowLoadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tallRowLoadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const waveformSlideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controlsHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomTabLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomTabLongPressTriggeredRef = useRef(false);
  const dateStripRef = useRef<HTMLDivElement | null>(null);
  const dateStripSyncingRef = useRef(false);
  const dateStripIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dateStripLastEmittedDayRef = useRef<number>(-1);
  const dateStripScrollEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dateStripPendingEmitRef = useRef<{ day: number; fraction: number } | null>(null);
  const dateStripDragRef = useRef<{ pointerId: number; startX: number; startScrollLeft: number } | null>(null);

  const showPrivilegedFixedSyncUi = useMemo(() => {
    return isAdmin;
  }, [isAdmin]);

  const [rowFractions, setRowFractions] = useState<[number, number, number]>([0.22, 0.28, 0.5]);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const resizeStateRef = useRef<{
    boundary: 0 | 1;
    startY: number;
    startFractions: [number, number, number];
    pointerId: number;
  } | null>(null);

  const minRowFraction = isMobile ? 0.1 : 0.08;
  const normalizedRowFractions = useMemo<[number, number, number]>(() => {
    const sum = rowFractions[0] + rowFractions[1] + rowFractions[2];
    if (!Number.isFinite(sum) || sum <= 0) return [0.22, 0.28, 0.5];
    return [rowFractions[0] / sum, rowFractions[1] / sum, rowFractions[2] / sum];
  }, [rowFractions]);
  const activeRowFractions = useMemo<[number, number, number]>(
    () => (isMobile ? [0.2, 0.3, 0.5] : normalizedRowFractions),
    [isMobile, normalizedRowFractions],
  );

  useEffect(() => {
    if (!skinRowFractionsProp) return;
    if (skinRowFractionsProp.some((value) => !Number.isFinite(value) || value <= 0)) return;
    setRowFractions((prev) => (
      prev.every((value, index) => Math.abs(value - skinRowFractionsProp[index]) < 0.0001)
        ? prev
        : skinRowFractionsProp
    ));
  }, [skinRowFractionsProp]);
  const shortWidgetSize = useMemo(() => ({ width: 12, height: isMobile ? 3 : 4 }), [isMobile]);
  const mediumWidgetSize = useMemo(() => ({ width: 12, height: isMobile ? 5 : 6 }), [isMobile]);
  const tallWidgetSize = useMemo(() => ({ width: 12, height: isMobile ? 9 : 12 }), [isMobile]);

  const applyBoundaryDrag = useCallback((fractions: [number, number, number], boundary: 0 | 1, deltaFraction: number) => {
    const next: [number, number, number] = [...fractions] as [number, number, number];
    const first = boundary;
    const second = (boundary + 1) as 1 | 2;
    const pairTotal = next[first] + next[second];

    let newFirst = next[first] + deltaFraction;
    let newSecond = pairTotal - newFirst;

    if (newFirst < minRowFraction) {
      newFirst = minRowFraction;
      newSecond = pairTotal - newFirst;
    }
    if (newSecond < minRowFraction) {
      newSecond = minRowFraction;
      newFirst = pairTotal - newSecond;
    }

    next[first] = newFirst;
    next[second] = newSecond;
    return next;
  }, [minRowFraction]);

  const startResize = useCallback((boundary: 0 | 1, event: React.PointerEvent<HTMLButtonElement>) => {
    if (!isAdmin) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeStateRef.current = {
      boundary,
      startY: event.clientY,
      startFractions: normalizedRowFractions,
      pointerId: event.pointerId,
    };
  }, [isAdmin, normalizedRowFractions]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const resizeState = resizeStateRef.current;
      if (!resizeState || !gridRef.current) return;
      if (event.pointerId !== resizeState.pointerId) return;

      const height = gridRef.current.clientHeight;
      if (height <= 0) return;

      const deltaFraction = (event.clientY - resizeState.startY) / height;
      const next = applyBoundaryDrag(resizeState.startFractions, resizeState.boundary, deltaFraction);
      setRowFractions(next);
      onSkinRowFractionsChange?.(next);
    };

    const handlePointerUp = () => {
      if (!resizeStateRef.current) return;
      resizeStateRef.current = null;
      onSkinRowFractionsCommit?.(activeRowFractions);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [activeRowFractions, applyBoundaryDrag, normalizedRowFractions, onSkinRowFractionsChange, onSkinRowFractionsCommit]);
  
  const headerRef = useRef<HTMLDivElement | null>(null);

  const scheduleControlsHide = useCallback(() => {
    if (controlsHideTimer.current) {
      clearTimeout(controlsHideTimer.current);
    }
    controlsHideTimer.current = setTimeout(() => {
      setControlsActive(false);
    }, 3000);
  }, []);

  const handleUserActivity = useCallback(() => {
    setControlsActive(true);
    scheduleControlsHide();
  }, [scheduleControlsHide]);

  useEffect(() => {
    // Show controls on initial render, then auto-hide after a short delay
    setControlsActive(true);
    scheduleControlsHide();
  }, [scheduleControlsHide]);

  useEffect(() => {
    const events: Array<keyof WindowEventMap> = ['mousemove', 'touchstart', 'touchmove', 'pointerdown'];
    events.forEach((evt) => window.addEventListener(evt, handleUserActivity, { passive: true }));
    return () => {
      events.forEach((evt) => window.removeEventListener(evt, handleUserActivity));
      if (controlsHideTimer.current) clearTimeout(controlsHideTimer.current);
    };
  }, [handleUserActivity]);

  // Debug logging for widget customizations and assignments (must be after shortWidgetSlides/tallWidgetSlides are declared)

  const syntheticCyclesWidget = useMemo<WidgetConfig | null>(() => {
    if (cycleRings.length === 0) return null;
    return {
      id: 'cycles',
      title: 'Interlocking Cycles',
      icon: <Layers className="h-4 w-4" />,
      component: (
        <div className="h-full w-full overflow-hidden">
          <MultiRing
            rings={cycleRings}
            hiddenRings={hiddenRings}
            onRingsChange={onRingsChange}
            onToggleRing={onToggleRing}
            gregorianDate={currentDate}
            onSyncToNow={onSyncToNow}
            onRotationChange={onRotationChange}
            hideToolbar
            constrainToContainer
            displayMode="multi"
            railOrientation="horizontal"
            ringThemeLayout={{ visibleRingCount: Math.max(1, Math.min(7, cycleRings.length)) }}
            maxRings={Math.max(1, Math.min(7, cycleRings.length))}
          />
        </div>
      ),
      defaultLayout: { x: 0, y: 0, w: 12, h: 8, minH: 6 },
    };
  }, [currentDate, cycleRings, hiddenRings, onRingsChange, onRotationChange, onSyncToNow, onToggleRing]);

  // Ensure widgets is always an array and always expose the cycles widget in skin mode.
  const safeWidgets = useMemo(() => {
    const baseWidgets = widgets ?? [];
    if (!syntheticCyclesWidget || baseWidgets.some((widget) => widget?.id === 'cycles')) {
      return baseWidgets;
    }
    return [...baseWidgets, syntheticCyclesWidget];
  }, [syntheticCyclesWidget, widgets]);

  const renderFallbackShort = useCallback((widget: WidgetConfig) => {
    const Icon = widget.icon ? () => widget.icon : undefined;
    return (
      <div className="w-full h-full flex flex-col justify-center gap-3 rounded-2xl border border-amber-400/30 bg-gradient-to-br from-teal-950/85 via-slate-900/70 to-teal-950/85 px-5 py-4 text-amber-100 shadow-[0_12px_30px_rgba(0,0,0,0.35)]">
        <div className="flex items-center gap-2 text-sm font-semibold">
          {Icon ? <Icon /> : <div className="w-2.5 h-2.5 rounded-full bg-amber-300" />}
          <span className="truncate">{widget.title}</span>
        </div>
        <p className="text-[11px] text-amber-200/75 leading-snug">
          Compact view is using the default layout. Edit the widget to supply a short view.
        </p>
      </div>
    );
  }, []);

  const renderEmptyRow = useCallback((label: string, hint: string) => (
    <div className="flex h-full w-full items-center justify-center px-4 py-3">
      <div className="flex w-full max-w-4xl flex-col items-center justify-center rounded-2xl border border-amber-400/20 bg-black/15 px-6 py-5 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/60">{label}</div>
        <div className="mt-2 text-sm font-medium text-amber-100/90">{hint}</div>
      </div>
    </div>
  ), []);

  const homeSelectorWidget = useMemo<WidgetConfig>(() => ({
    id: HOME_WIDGET_ID,
    title: 'Home',
    component: null,
    defaultLayout: { x: 0, y: 0, w: 1, h: 1 },
  }), []);

  const getVisibility = useCallback((widget?: WidgetConfig) => {
    const widgetId = widget?.id;
    if (!widgetId) return { showShort: false, showMedium: false, showTall: false };
    if (widgetId === HOME_WIDGET_ID) return { showShort: false, showMedium: false, showTall: true };
    if (widgetId === 'settings') return { showShort: false, showMedium: false, showTall: false };
    if (widgetId === 'timeline') return { showShort: false, showMedium: false, showTall: false };
    if (widgetId === 'waveform') return { showShort: true, showMedium: true, showTall: true };
    if (widgetId === 'year-ring') return { showShort: false, showMedium: false, showTall: true };
    const config = widgetCustomizations?.[widgetId] ?? {};
    const raw = config.slideRow as unknown;
    const normalizedSlideRow = raw === '2' || raw === 2 ? 2 : raw === '1' || raw === 1 ? 1 : raw === '0' || raw === 0 ? 0 : undefined;
    const autoShowShort = widget?.alwaysShowInShort || !!(widget?.shortComponent);
    const autoShowMedium = !!(widget?.shortComponentTall);
    const showShort = typeof config.showInShort === 'boolean'
      ? config.showInShort
      : normalizedSlideRow === 1
        ? true
        : autoShowShort;
    const showMedium = typeof (config as any).showInMedium === 'boolean'
      ? (config as any).showInMedium
      : normalizedSlideRow === 2
        ? true
        : autoShowMedium;
    // showTall drives the bottom nav / toolbar row. Core toolbar widgets are ALWAYS present
    // (protected — no toggle can ever drop them, so the nav can't break). Other widgets opt IN
    // via the clean tallRowVisible field. We deliberately do NOT read legacy showInTall here:
    // it accumulated stray values while the Bottom Row selector was broken, and honoring it
    // dropped nav items. tallRowVisible starts empty for everyone, so the nav is unchanged until
    // the user explicitly adds a widget.
    const showTall = TOOLBAR_WIDGET_IDS.has(widgetId) || (config as any).tallRowVisible === true;
    return { showShort: !!showShort, showMedium: !!showMedium, showTall };
  }, [widgetCustomizations]);

  // Short slider: include all widgets marked as short in customizations, even if not in safeWidgets
  const shortWidgetSlides = useMemo(() => {
    const widgetById = new Map(safeWidgets.map((widget) => [widget.id, widget]));
    const orderedIds = safeWidgets.map((widget) => widget.id);
    const customOnlyIds = Object.keys(widgetCustomizations || {})
      .filter((id) => !widgetById.has(id))
      .sort((a, b) => a.localeCompare(b));
    // For each widget ID, get the widget config (from safeWidgets or fallback)
    const allCandidates = [...orderedIds, ...customOnlyIds].map(
      (id) =>
        widgetById.get(id) || {
          id,
          title: id,
          component: null,
          defaultLayout: { x: 0, y: 0, w: 6, h: 2 },
        }
    );
    // Filter for those that should show in short slider
    const filtered = allCandidates.filter((widget) => {
      const { showShort } = getVisibility(widget);
      return showShort && !shortSliderExcludedWidgetIds.has(widget.id);
    });
    const slides = filtered.map((widget, index) => ({
      key: `short-${widget.id ?? index}`,
      widget,
      index,
    }));
    if (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        (typeof (window as any).DEBUG_DASHBOARD_SKIN !== 'undefined' && (window as any).DEBUG_DASHBOARD_SKIN))
    ) {
      // eslint-disable-next-line no-console
      console.log('[DreamspellSkin] shortWidgetSlides:', slides);
    }
    return slides;
  }, [safeWidgets, widgetCustomizations, getVisibility, shortSliderExcludedWidgetIds]);

  // Tall slider: widgets with slideRow=0 (default)
  const tallWidgetSlides = useMemo(() => {
    const filtered = safeWidgets.filter(w => {
      const { showTall } = getVisibility(w);
      return showTall;
    });
    const preferredTallOrder = ['grid', 'gregorian-grid', 'year-ring', 'waveform', 'ai-day-summary', 'reminders', 'timeline', 'agenda', 'cycles'];
    const rank = (id: string) => {
      const idx = preferredTallOrder.indexOf(id);
      return idx === -1 ? preferredTallOrder.length : idx;
    };
    filtered.sort((a, b) => {
      const rankDiff = rank(a.id) - rank(b.id);
      if (rankDiff !== 0) return rankDiff;
      return a.title.localeCompare(b.title);
    });
    return filtered.map((widget, index) => ({
      key: `tall-${widget.id ?? index}`,
      widget,
      index,
    }));
  }, [getVisibility, safeWidgets]);

  // Medium slider: widgets with shortComponentTall (middle row)
  // Pulls from all known sources so pinned widgets (timeline, waveform) always appear
  // even if they were moved to hiddenWidgets in saved config.
  const mediumWidgetSlides = useMemo(() => {
    const preferredMediumOrder = ['waveform', 'timeline', 'space-weather-waveforms'];
    const allSources = [...safeWidgets, ...(hiddenWidgets ?? []), ...(addableWidgets ?? [])];
    const seen = new Set<string>();
    const deduped = allSources.filter(w => {
      if (!w?.id || seen.has(w.id)) return false;
      seen.add(w.id);
      return true;
    });
    const filtered = deduped.filter(w => {
      const { showMedium } = getVisibility(w);
      return showMedium && !shortSliderExcludedWidgetIds.has(w.id);
    });
    filtered.sort((a, b) => {
      const ai = preferredMediumOrder.indexOf(a.id);
      const bi = preferredMediumOrder.indexOf(b.id);
      const ar = ai === -1 ? preferredMediumOrder.length : ai;
      const br = bi === -1 ? preferredMediumOrder.length : bi;
      return ar - br;
    });
    return filtered.map((widget, index) => ({
      key: `medium-${widget.id ?? index}`,
      widget,
      index,
    }));
  }, [addableWidgets, getVisibility, hiddenWidgets, safeWidgets, shortSliderExcludedWidgetIds]);

  const allKnownWidgets = useMemo(() => {
    const merged = [...safeWidgets, ...(hiddenWidgets ?? []), ...(addableWidgets ?? [])];
    const byId = new Map<string, WidgetConfig>();
    for (const widget of merged) {
      if (widget?.id && !byId.has(widget.id)) {
        byId.set(widget.id, widget);
      }
    }
    return Array.from(byId.values());
  }, [addableWidgets, hiddenWidgets, safeWidgets]);

  const cycleLayoutWidgets = useMemo(() => {
    const preferredCycleWidgetIds = [
      'cycles',
      'year-ring',
      'grid',
      'gregorian-grid',
      'timeline',
      'waveform',
      'legend',
      'ai-day-summary',
      'agenda',
      'reminders',
    ];
    const byId = new Map(allKnownWidgets.map((widget) => [widget.id, widget]));
    const orderedPreferred = preferredCycleWidgetIds
      .map((widgetId) => byId.get(widgetId))
      .filter((widget): widget is WidgetConfig => Boolean(widget));
    const detected = allKnownWidgets.filter((widget) => {
      if (preferredCycleWidgetIds.includes(widget.id)) return false;
      const token = `${widget.id} ${widget.title}`.toLowerCase();
      return (
        token.includes('cycle') ||
        token.includes('ring') ||
        token.includes('wave') ||
        token.includes('legend') ||
        token.includes('summary')
      );
    });
    const ordered = [...orderedPreferred, ...detected];
    const deduped = new Map<string, WidgetConfig>();
    for (const widget of ordered) {
      if (!deduped.has(widget.id)) deduped.set(widget.id, widget);
    }
    return Array.from(deduped.values());
  }, [allKnownWidgets]);

  const selectorWidgets = useMemo(() => {
    const byId = new Map<string, WidgetConfig>();

    // Include cycle layout widgets so selector can control cycle-specific entries.
    for (const widget of cycleLayoutWidgets) {
      if (widget?.id && !byId.has(widget.id)) {
        byId.set(widget.id, widget);
      }
    }

    // Include all known widgets with at least one display component.
    // This ensures the selector lists are stable regardless of current visibility state.
    for (const widget of allKnownWidgets) {
      if (!widget?.id || byId.has(widget.id)) continue;
      if (widget.component || widget.shortComponent || widget.shortComponentTall) {
        byId.set(widget.id, widget);
      }
    }

    return [homeSelectorWidget, ...Array.from(byId.values())]
      .filter((widget) => typeof widget?.id === 'string' && widget.id.trim().length > 0)
      .sort((a, b) => (a.title || a.id).localeCompare(b.title || b.id));
  }, [allKnownWidgets, cycleLayoutWidgets, homeSelectorWidget]);

  const getWidgetDisplayName = useCallback((widget: WidgetConfig) => {
    const title = typeof widget?.title === 'string' ? widget.title.trim() : '';
    if (title.length > 0) return title;
    return widget.id;
  }, []);

  useEffect(() => {
    const nextDefaults: Partial<Record<RowName, string>> = {};
    const persistedDefaults = widgetCustomizations?.[DREAMSPELL_LAYOUT_DEFAULTS_KEY]?.dreamspellRowDefaults;
    if (persistedDefaults && typeof persistedDefaults === 'object') {
      for (const row of VALID_ROW_NAMES) {
        const widgetId = persistedDefaults[row];
        if (typeof widgetId === 'string' && widgetId.length > 0) {
          nextDefaults[row] = widgetId;
        }
      }
    }

    if (Object.keys(nextDefaults).length === 0) {
      for (const [widgetId, customization] of Object.entries(widgetCustomizations ?? {})) {
        const row = customization?.dreamspellDefaultRow;
        if (row === 'short' || row === 'medium' || row === 'tall') {
          nextDefaults[row] = widgetId;
        }
      }
    }

    setDefaultWidgetByRow((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(nextDefaults);
      if (prevKeys.length !== nextKeys.length) return nextDefaults;
      for (const key of nextKeys) {
        const row = key as RowName;
        if (prev[row] !== nextDefaults[row]) return nextDefaults;
      }
      return prev;
    });
  }, [widgetCustomizations]);

  const applyCycleWidgetVisibility = useCallback((
    widgetId: string,
    nextVisibility: { showInShort: boolean; showInMedium: boolean; showInTall: boolean },
  ) => {
    if (!onWidgetCustomizationChange) {
      toast.error('Cycle layout editing is not available in this view.');
      return;
    }

    const existing = widgetCustomizations?.[widgetId] ?? {};
    const normalizedVisibility = widgetId === HOME_WIDGET_ID
      ? {
          showInShort: false,
          showInMedium: false,
          showInTall: nextVisibility.showInTall,
        }
      : nextVisibility;
    const enabledRows = [
      normalizedVisibility.showInShort ? 1 : null,
      normalizedVisibility.showInMedium ? 2 : null,
      normalizedVisibility.showInTall ? 0 : null,
    ].filter((row): row is 0 | 1 | 2 => row !== null);

    const slideRow: 0 | 1 | 2 | undefined = enabledRows.length === 1
      ? enabledRows[0]
      : (existing.slideRow as 0 | 1 | 2 | undefined);

    onWidgetCustomizationChange(widgetId, {
      slideRow,
      showInShort: normalizedVisibility.showInShort,
      showInMedium: normalizedVisibility.showInMedium,
      showInTall: normalizedVisibility.showInTall,
      // Clean field the bottom-nav reads (see getVisibility). Legacy showInTall is kept for
      // any other consumers but is no longer authoritative for nav membership.
      tallRowVisible: normalizedVisibility.showInTall,
    });

    // Keep defaults synchronized: if a widget is removed from a row,
    // clear that row's default when it points to this widget, and persist the clear.
    const nextDefaults: Partial<Record<RowName, string>> = { ...defaultWidgetByRow };
    let defaultsChanged = false;
    if (!normalizedVisibility.showInShort && nextDefaults.short === widgetId) {
      delete nextDefaults.short;
      defaultsChanged = true;
    }
    if (!normalizedVisibility.showInMedium && nextDefaults.medium === widgetId) {
      delete nextDefaults.medium;
      defaultsChanged = true;
    }
    if (!normalizedVisibility.showInTall && nextDefaults.tall === widgetId) {
      delete nextDefaults.tall;
      defaultsChanged = true;
    }
    if (widgetId === HOME_WIDGET_ID && !normalizedVisibility.showInTall && isHomeWidgetActive) {
      setIsHomeWidgetActive(false);
    }
    if (defaultsChanged) {
      setDefaultWidgetByRow(nextDefaults);
      onWidgetCustomizationChange(DREAMSPELL_LAYOUT_DEFAULTS_KEY, {
        dreamspellRowDefaults: nextDefaults,
      });
    }
  }, [defaultWidgetByRow, isHomeWidgetActive, onWidgetCustomizationChange, widgetCustomizations]);

  const getVisibilityForRow = useCallback((
    visibility: { showShort: boolean; showMedium: boolean; showTall: boolean },
    row: RowName,
  ) => {
    if (row === 'short') return visibility.showShort;
    if (row === 'medium') return visibility.showMedium;
    return visibility.showTall;
  }, []);

  const toggleCycleWidgetRow = useCallback((widget: WidgetConfig) => {
    const visibility = getVisibility(widget);
    const nextEnabled = !(visibility.showShort && visibility.showMedium && visibility.showTall);
    applyCycleWidgetVisibility(widget.id, {
      showInShort: nextEnabled,
      showInMedium: nextEnabled,
      showInTall: nextEnabled,
    });
  }, [applyCycleWidgetVisibility, getVisibility]);

  const setCycleWidgetColumnVisibility = useCallback((row: RowName, enabled: boolean) => {
    const widgetsToUpdate = selectorWidgets
      .filter((widget) => typeof widget.id === 'string' && widget.id.trim().length > 0)
      .filter((widget) => row === 'tall' || widget.id !== HOME_WIDGET_ID);
    if (widgetsToUpdate.length === 0) return;

    for (const widget of widgetsToUpdate) {
      const visibility = getVisibility(widget);
      applyCycleWidgetVisibility(widget.id, {
        showInShort: row === 'short' ? enabled : visibility.showShort,
        showInMedium: row === 'medium' ? enabled : visibility.showMedium,
        showInTall: row === 'tall' ? enabled : visibility.showTall,
      });
    }
  }, [applyCycleWidgetVisibility, getVisibility, selectorWidgets]);

  const toggleCycleWidgetColumn = useCallback((row: RowName) => {
    const widgetsToUpdate = selectorWidgets
      .filter((widget) => typeof widget.id === 'string' && widget.id.trim().length > 0)
      .filter((widget) => row === 'tall' || widget.id !== HOME_WIDGET_ID);
    if (widgetsToUpdate.length === 0) return;

    const allEnabled = widgetsToUpdate.every((widget) => getVisibilityForRow(getVisibility(widget), row));
    setCycleWidgetColumnVisibility(row, !allEnabled);
  }, [getVisibility, getVisibilityForRow, selectorWidgets, setCycleWidgetColumnVisibility]);

  const setAllCycleWidgetVisibility = useCallback((enabled: boolean) => {
    const widgetsToUpdate = selectorWidgets.filter((widget) => typeof widget.id === 'string' && widget.id.trim().length > 0);
    if (widgetsToUpdate.length === 0) return;

    for (const widget of widgetsToUpdate) {
      applyCycleWidgetVisibility(widget.id, {
        showInShort: enabled,
        showInMedium: enabled,
        showInTall: enabled,
      });
    }
  }, [applyCycleWidgetVisibility, selectorWidgets]);

  const mainWidgetSlide = useMemo(() => {
    const indexedTallSlide = tallWidgetSlides[tallSlideIndex];
    if (indexedTallSlide) return indexedTallSlide;
    
    const preferredMainWidget =
      (selectedCycleWidgetId
        ? allKnownWidgets.find((widget) => widget.id === selectedCycleWidgetId) ??
          cycleLayoutWidgets.find((widget) => widget.id === selectedCycleWidgetId)
        : null) ??
      allKnownWidgets.find((widget) => widget.id === 'ai-day-summary') ??
      allKnownWidgets.find((widget) => widget.id === 'gregorian-grid') ??
      allKnownWidgets.find((widget) => widget.id === 'timeline') ??
      allKnownWidgets.find((widget) => widget.id === 'agenda') ??
      cycleLayoutWidgets.find((widget) => widget.id === 'cycles') ??
      allKnownWidgets.find((widget) => widget.id === 'cycles') ??
      allKnownWidgets.find((widget) => widget.id === 'grid') ??
      cycleWidget ??
      tallWidgetSlides[0]?.widget;
    if (!preferredMainWidget) return null;
    return (
      tallWidgetSlides.find((slide) => slide.widget.id === preferredMainWidget.id) ?? {
        key: `main-${preferredMainWidget.id}`,
        widget: preferredMainWidget,
        index: 0,
      }
    );
  }, [allKnownWidgets, cycleLayoutWidgets, cycleWidget, selectedCycleWidgetId, tallSlideIndex, tallWidgetSlides]);

  const galacticData = useMemo(() => {
    const day13 = gregorianToDay13(currentDate);
    const monthInfo = MONTH_NAMES.find(m => m.number === day13.month);
    const weekdayName = WEEKDAY_NAMES[day13.weekday] || '';
    const moonPhase = getMoonPhase(currentDate);
    const moonPhaseName = getMoonPhaseName(moonPhase);
    
    return {
      day13,
      monthInfo,
      weekdayName,
      moonPhase,
      moonPhaseName,
      gregorianTime: format(currentDate, 'HH:mm:ss'),
      gregorianDate: format(currentDate, 'EEEE, MMMM d, yyyy'),
    };
  }, [currentDate]);
  const { day13, monthInfo, weekdayName, gregorianDate, moonPhaseName } = galacticData;

  const headerCardStyle = {
    background: 'linear-gradient(135deg, rgba(15,60,65,0.85) 0%, rgba(10,45,50,0.9) 50%, rgba(8,35,40,0.85) 100%)',
    boxShadow: '0 4px 30px rgba(0,0,0,0.3), inset 0 1px 0 rgba(212,165,116,0.2), 0 0 60px rgba(212,165,116,0.05)',
  } as const;

  const headerCardOverlayStyle = {
    background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(244,208,63,0.08) 0%, transparent 60%)',
  } as const;

  // Shared header card reused in short slider
  const headerCard = useMemo(() => (
    <div
      className="relative w-full h-full px-6 py-4 rounded-2xl overflow-hidden flex flex-col justify-center"
      style={headerCardStyle}
    >
      <div
        className="absolute inset-0 rounded-2xl"
        style={headerCardOverlayStyle}
      />

      <div className="relative space-y-3">
        <div
          className="text-base font-semibold text-amber-200/90 hover:text-amber-100 transition-colors cursor-pointer flex items-center justify-center gap-2"
          onClick={onSyncToNow}
          title="Click to sync to now"
        >
          {gregorianDate}
        </div>

        <div className="text-xl md:text-2xl font-bold tracking-wide">
          {day13.isOutOfTime ? (
            <span
              className="inline-block"
              style={{
                background: 'linear-gradient(135deg, #f4d03f 0%, #d4a574 50%, #c9a869 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 30px rgba(244,208,63,0.3)',
              }}
            >
              ✧ Day Out of Time ✧
            </span>
          ) : (
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <span
                style={{
                  background: 'linear-gradient(135deg, #c9a869 0%, #f4d03f 50%, #d4a574 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {weekdayName}
              </span>
              <span className="text-amber-400/30 text-lg">·</span>
              <span
                style={{
                  color: `hsl(var(--month-${day13.month}))`,
                  textShadow: `0 0 20px hsl(var(--month-${day13.month}) / 0.3)`,
                }}
              >
                {monthInfo?.glyph} {monthInfo?.name}
              </span>
              <span className="text-amber-400/30 text-lg">·</span>
              <span
                style={{
                  background: 'linear-gradient(135deg, #7eb8b5 0%, #a8d4cf 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Day {day13.day}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-4 text-sm">
          <div className="flex items-center gap-2 text-amber-200/80">
            <Clock className="w-3.5 h-3.5" />
            <span>{galacticData.gregorianTime}</span>
          </div>
          <div className="w-px h-4 bg-amber-400/30" />
          <div className="flex items-center gap-2 text-teal-300/80">
            <Moon className="w-3.5 h-3.5" />
            <span>{moonPhaseName}</span>
          </div>
        </div>
      </div>
    </div>
  ), [day13, galacticData.gregorianTime, gregorianDate, monthInfo?.glyph, monthInfo?.name, moonPhaseName, onSyncToNow, weekdayName]);

  const effectiveShortSlides = useMemo(() => {
    if (shortWidgetSlides.length > 0) {
      const preferredTopOrder = ['cycles', 'year-ring', 'timeline', 'gregorian-grid', 'grid'];
      const rank = (widgetId?: string) => {
        const index = preferredTopOrder.indexOf(widgetId ?? '');
        return index === -1 ? preferredTopOrder.length : index;
      };
      return [...shortWidgetSlides].sort((a, b) => {
        const rankDiff = rank(a.widget.id) - rank(b.widget.id);
        if (rankDiff !== 0) return rankDiff;
        return (a.widget.title ?? '').localeCompare(b.widget.title ?? '');
      });
    }
    const headerWidget: WidgetConfig = {
      id: '__header__',
      title: 'Today',
      component: headerCard,
      shortComponent: headerCard,
      defaultLayout: { x: 0, y: 0, w: 6, h: 2 },
    };
    return [{ key: 'short-__header__', widget: headerWidget, index: -1 }];
  }, [headerCard, shortWidgetSlides]);

  const maxShortSlide = Math.max(0, effectiveShortSlides.length - 1);
  const maxMediumSlide = Math.max(0, mediumWidgetSlides.length - 1);
  const maxTallSlide = Math.max(0, tallWidgetSlides.length - 1);
  const shortSlideCount = Math.max(1, effectiveShortSlides.length);
  const mediumSlideCount = Math.max(1, mediumWidgetSlides.length);
  const tallSlideCount = Math.max(1, tallWidgetSlides.length);
  const activeShortSlide = effectiveShortSlides[shortSlideIndex] ?? null;
  const activeTopWidget = activeShortSlide?.widget ?? null;
  const activeMediumSlide = mediumWidgetSlides[mediumSlideIndex] ?? null;
  const lockedTopWidget = useMemo(
    () => activeTopWidget ?? allKnownWidgets.find((widget) => widget.id === 'cycles') ?? null,
    [activeTopWidget, allKnownWidgets],
  );
  const topCycleVisibleCount = useMemo(() => {
    if (cycleRings.length <= 0) return 1;
    if (cycleRings.length >= 7) return 7;
    return cycleRings.length;
  }, [cycleRings.length]);
  const topGridTemplateRows = useMemo(() => {
    const medium = isMobile ? '6.5rem' : '8rem';
    const tall = isMobile ? '13rem' : '14rem';
    const activeId = isHomeWidgetActive ? HOME_WIDGET_ID : (mainWidgetSlide?.widget.id ?? null);
    const hideStripAndMedium = isTallExpanded || activeId === HOME_WIDGET_ID || activeId === 'year-ring';
    if (hideStripAndMedium) {
      return `1fr 0px 0px 1fr`;
    }
    return `1fr 2.4rem ${medium} ${tall}`;
  }, [isMobile, isTallExpanded, isHomeWidgetActive, mainWidgetSlide?.widget.id]);

  const setPrimaryPanelWidget = useCallback((widgetId: string) => {
    setIsHomeWidgetActive(false);
    // Always update the selected widget ID (for cycle layout widgets)
    onSelectCycleWidget?.(widgetId);
    // If the widget is in tallWidgetSlides, also update the slide index
    const nextIndex = tallWidgetSlides.findIndex((slide) => slide.widget.id === widgetId);
    if (nextIndex >= 0) {
      setTallSlideIndex(nextIndex);
    } else {
      // For cycle-only widgets not in tallWidgetSlides, clear the index so mainWidgetSlide checks selectedCycleWidgetId
      setTallSlideIndex(-1);
    }
  }, [onSelectCycleWidget, tallWidgetSlides]);

  const bottomNavWidgets = useMemo(() => {
    // Primary: widgets from tallWidgetSlides (respects customization)
    const widgets = tallWidgetSlides
      .map((slide) => slide.widget)
      .filter((widget): widget is WidgetConfig => Boolean(widget?.id));
    
    // Secondary: add cycle layout widgets customized for tall row, even if not in safeWidgets
    const additionalWidgets = cycleLayoutWidgets.filter(w => {
      const { showTall } = getVisibility(w);
      return showTall && !widgets.some(existing => existing.id === w.id);
    });
    
    const includeHome = getVisibility(homeSelectorWidget).showTall;
    const combined = [
      ...(includeHome ? [homeSelectorWidget] : []),
      ...widgets,
      ...additionalWidgets,
    ];
    if (combined.length > 0) return combined;
    
    // Fallback: all known widgets
    return allKnownWidgets.filter((widget): widget is WidgetConfig => Boolean(widget?.id));
  }, [allKnownWidgets, cycleLayoutWidgets, getVisibility, homeSelectorWidget, tallWidgetSlides]);
  const nonHomeBottomNavWidgets = useMemo(
    () => bottomNavWidgets.filter((widget) => widget.id !== HOME_WIDGET_ID),
    [bottomNavWidgets],
  );

  const timelineBandWidget = useMemo(
    () =>
      mediumWidgetSlides[0]?.widget ??
      allKnownWidgets.find((widget) => widget.id === 'timeline') ??
      null,
    [allKnownWidgets, mediumWidgetSlides],
  );

  const ringHeroWidget = useMemo(
    () =>
      allKnownWidgets.find((widget) => widget.id === 'cycles') ??
      allKnownWidgets.find((widget) => widget.id === 'year-ring') ??
      null,
    [allKnownWidgets],
  );
  const activeTimelineWidget = activeMediumSlide?.widget ?? timelineBandWidget;
  const activeMainWidgetId = isHomeWidgetActive ? HOME_WIDGET_ID : (mainWidgetSlide?.widget.id ?? null);
  const isMediumWaveformActive = activeTimelineWidget?.id === 'waveform';
  const isTopCyclesActive = lockedTopWidget?.id === 'cycles';
  const canShowMoonCalendar = tallWidgetSlides.some((slide) => slide.widget.id === 'grid');
  const canShowDayCalendar = tallWidgetSlides.some((slide) => slide.widget.id === 'gregorian-grid');

  const rowDefaultOptions = useMemo(() => {
    const byId = new Map(selectorWidgets.map((widget) => [widget.id, widget]));
    const resolveWidget = (widgetId?: string) => {
      if (!widgetId) return undefined;
      return byId.get(widgetId)
        ?? allKnownWidgets.find((widget) => widget.id === widgetId)
        ?? cycleLayoutWidgets.find((widget) => widget.id === widgetId);
    };

    const withDefault = (widgets: WidgetConfig[], row: RowName) => {
      const defaultWidget = resolveWidget(defaultWidgetByRow[row]);
      if (!defaultWidget) return widgets;
      return widgets.some((widget) => widget.id === defaultWidget.id)
        ? widgets
        : [defaultWidget, ...widgets];
    };

    const short = withDefault(selectorWidgets.filter((widget) => getVisibility(widget).showShort), 'short');
    const medium = withDefault(selectorWidgets.filter((widget) => getVisibility(widget).showMedium), 'medium');
    const tall = withDefault(selectorWidgets.filter((widget) => getVisibility(widget).showTall), 'tall');
    return { short, medium, tall };
  }, [allKnownWidgets, cycleLayoutWidgets, defaultWidgetByRow, getVisibility, selectorWidgets]);
  const showMediumRowArrows = mediumWidgetSlides.length > 1 && rowDefaultOptions.medium.length !== 1;
  const hideCycleOverlayArrows =
    lockedTopWidget?.id === 'cycles' ||
    activeMainWidgetId === 'cycles' ||
    activeTimelineWidget?.id === 'cycles';
  const showMediumRowNavArrows = showMediumRowArrows && !hideCycleOverlayArrows;

  const mobileConfigWidget = useMemo(
    () => selectorWidgets.find((widget) => widget.id === mobileConfigWidgetId) ?? null,
    [mobileConfigWidgetId, selectorWidgets],
  );

  const mobileConfigWidgetVisibility = useMemo(
    () => (mobileConfigWidget ? getVisibility(mobileConfigWidget) : { showShort: false, showMedium: false, showTall: false }),
    [getVisibility, mobileConfigWidget],
  );

  const selectedMobileRing = useMemo(
    () => cycleRings.find((ring) => ring.id === mobileConfigRingId) ?? null,
    [cycleRings, mobileConfigRingId],
  );

  const selectedMobileCycleGroup = useMemo(
    () => mobileCycleGroups.find((group) => group.id === mobileSelectedCycleGroupId) ?? null,
    [mobileCycleGroups, mobileSelectedCycleGroupId],
  );

  const mobileAddableRingPresets = useMemo(() => {
    const existingIds = new Set(cycleRings.map((ring) => ring.id));
    const preferredOrder: Record<string, number> = {
      lunar: 0,
      earth: 1,
      mercury: 2,
      venus: 3,
      mars: 4,
      jupiter: 5,
      saturn: 6,
      uranus: 7,
      neptune: 8,
      atomicHour: 9,
      atomicMinute: 10,
      atomicSecond: 11,
    };
    return Object.values(PRESET_CYCLES)
      .filter((preset) => !existingIds.has(preset.id))
      .sort((a, b) => {
        const rankA = preferredOrder[a.id] ?? 999;
        const rankB = preferredOrder[b.id] ?? 999;
        if (rankA !== rankB) return rankA - rankB;
        return a.name.localeCompare(b.name);
      });
  }, [cycleRings]);

  const mobileWidgetCustomization = useMemo(
    () => (mobileConfigWidgetId ? (widgetCustomizations?.[mobileConfigWidgetId] ?? {}) : {}),
    [mobileConfigWidgetId, widgetCustomizations],
  );

  const mobileWidgetLabelIcon = useMemo(
    () => (typeof mobileWidgetCustomization?.widgetLabelIcon === 'string' ? mobileWidgetCustomization.widgetLabelIcon : ''),
    [mobileWidgetCustomization],
  );

  const mobileWidgetLabelName = useMemo(
    () => {
      if (!mobileConfigWidget) return '';
      return (typeof mobileWidgetCustomization?.title === 'string' && mobileWidgetCustomization.title.length > 0)
        ? mobileWidgetCustomization.title
        : mobileConfigWidget.title;
    },
    [mobileConfigWidget, mobileWidgetCustomization],
  );

  const mobileWidgetFixedTimestamp = useMemo(
    () => (typeof mobileWidgetCustomization?.fixedTimestamp === 'string' ? mobileWidgetCustomization.fixedTimestamp : ''),
    [mobileWidgetCustomization],
  );

  const patchMobileRing = useCallback((patch: Partial<CycleRing>) => {
    if (!selectedMobileRing || !onRingsChange) return;
    const nextRings = cycleRings.map((ring) => {
      if (ring.id !== selectedMobileRing.id) return ring;
      const nextRing = { ...ring, ...patch };
      if (typeof nextRing.stages === 'number') {
        nextRing.stages = Math.max(1, Math.round(nextRing.stages));
      }
      if (typeof nextRing.waveformIntervalMultiplier === 'number') {
        nextRing.waveformIntervalMultiplier = Math.max(1, Math.round(nextRing.waveformIntervalMultiplier));
      }
      if (typeof nextRing.waveformIntervalDivider === 'number') {
        nextRing.waveformIntervalDivider = Math.max(1, Math.round(nextRing.waveformIntervalDivider));
      }
      if (typeof nextRing.stagesPerParent === 'number') {
        nextRing.stagesPerParent = Math.max(1, Math.round(nextRing.stagesPerParent));
      }
      return nextRing;
    });
    onRingsChange(nextRings);
  }, [cycleRings, onRingsChange, selectedMobileRing]);

  const applyStageIconsToRing = useCallback((icons: string[]) => {
    if (!selectedMobileRing || !onRingsChange || icons.length === 0) return;
    const stageCount = Math.max(1, selectedMobileRing.stages ?? icons.length);
    const existingLabels = Array.isArray(selectedMobileRing.labels) ? selectedMobileRing.labels : [];
    const nextLabels = Array.from({ length: stageCount }, (_, index) => {
      const icon = icons[index % icons.length] ?? '•';
      const fallbackLabel = existingLabels[index] ?? `Stage ${index + 1}`;
      const stripped = String(fallbackLabel).replace(/^[^\s]+\s+/, '');
      return `${icon} ${stripped || `Stage ${index + 1}`}`;
    });
    patchMobileRing({ labels: nextLabels });
  }, [onRingsChange, patchMobileRing, selectedMobileRing]);

  const handleApplySavedCycleGroup = useCallback(() => {
    if (!selectedMobileCycleGroup || !onRingsChange) return;
    const groupRings = Array.isArray(selectedMobileCycleGroup.rings)
      ? selectedMobileCycleGroup.rings.map((ring) => ({ ...ring }))
      : [];
    if (groupRings.length === 0) {
      toast.error('Selected cycle group has no rings.');
      return;
    }
    onRingsChange(groupRings);
    if (groupRings[0]) {
      setMobileConfigRingId(groupRings[0].id);
    }
    toast.success(`Applied ${selectedMobileCycleGroup.name}.`);
  }, [onRingsChange, selectedMobileCycleGroup]);

  const handleSaveCurrentAsCycleGroup = useCallback(async () => {
    const name = mobileCycleGroupName.trim();
    if (!name) {
      toast.error('Enter a cycle group name first.');
      return;
    }
    const id = `group-${Date.now()}`;
    const nextGroup: CycleGroupData = {
      id,
      name,
      description: mobileCycleGroupDescription.trim() || undefined,
      rings: cycleRings.map((ring) => ({ ...ring })),
    };
    try {
      await saveCycleGroup(undefined, nextGroup);
      const updated = await loadCycleGroups(undefined);
      setMobileCycleGroups(updated);
      setMobileSelectedCycleGroupId(id);
      toast.success('Cycle group saved.');
    } catch {
      toast.error('Failed to save cycle group.');
    }
  }, [cycleRings, mobileCycleGroupDescription, mobileCycleGroupName]);

  const handleUpdateCycleGroup = useCallback(async () => {
    if (!selectedMobileCycleGroup) {
      toast.error('Select a cycle group to update.');
      return;
    }
    const name = mobileCycleGroupName.trim();
    if (!name) {
      toast.error('Cycle group name is required.');
      return;
    }
    const nextGroup: CycleGroupData = {
      ...selectedMobileCycleGroup,
      name,
      description: mobileCycleGroupDescription.trim() || undefined,
      rings: cycleRings.map((ring) => ({ ...ring })),
    };
    try {
      await saveCycleGroup(undefined, nextGroup);
      const updated = await loadCycleGroups(undefined);
      setMobileCycleGroups(updated);
      toast.success('Cycle group updated.');
    } catch {
      toast.error('Failed to update cycle group.');
    }
  }, [cycleRings, mobileCycleGroupDescription, mobileCycleGroupName, selectedMobileCycleGroup]);

  const handleDeleteCycleGroup = useCallback(async () => {
    if (!selectedMobileCycleGroup) {
      toast.error('Select a cycle group to delete.');
      return;
    }
    const confirmed = window.confirm(`Delete cycle group "${selectedMobileCycleGroup.name}"?`);
    if (!confirmed) return;
    try {
      await deleteCycleGroup(undefined, selectedMobileCycleGroup.id);
      const updated = await loadCycleGroups(undefined);
      setMobileCycleGroups(updated);
      const nextSelected = updated[0]?.id ?? '';
      setMobileSelectedCycleGroupId(nextSelected);
      toast.success('Cycle group deleted.');
    } catch {
      toast.error('Failed to delete cycle group.');
    }
  }, [selectedMobileCycleGroup]);

  useEffect(() => {
    let active = true;
    const handleGroups = (groups: CycleGroupData[]) => {
      if (!active) return;
      setMobileCycleGroups(groups);
    };
    const unsubscribe = subscribeCycleGroups(undefined, handleGroups);
    if (!unsubscribe) {
      loadCycleGroups(undefined).then(handleGroups).catch(() => undefined);
    }
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!selectedMobileCycleGroup) return;
    setMobileCycleGroupName(selectedMobileCycleGroup.name ?? '');
    setMobileCycleGroupDescription(selectedMobileCycleGroup.description ?? '');
  }, [selectedMobileCycleGroup]);

  const handleAiWidgetLabelSetup = useCallback(() => {
    if (!mobileConfigWidget) return;
    const preset = getAiWidgetPreset(mobileConfigWidget);
    onWidgetCustomizationChange?.(mobileConfigWidget.id, {
      widgetLabelIcon: preset.icon,
      title: preset.name,
    });
    if (selectedMobileRing) {
      applyStageIconsToRing(preset.stageIcons);
      setMobileStageIconsInput(preset.stageIcons.join(','));
    }
    toast.success('AI setup applied for widget label and stage icons.');
  }, [applyStageIconsToRing, mobileConfigWidget, onWidgetCustomizationChange, selectedMobileRing]);

  const handleAddMobileCycleRingPreset = useCallback((overrideId?: string) => {
    if (!onRingsChange) {
      toast.error('Cycle layout editing is not available in this view.');
      return;
    }
    const id = overrideId ?? mobileAddRingPresetId;
    if (!id) {
      toast.error('Select a cycle ring preset first.');
      return;
    }
    const preset = PRESET_CYCLES[id];
    if (!preset) {
      toast.error('Selected cycle preset was not found.');
      return;
    }
    const existingRing = cycleRings.find((ring) => ring.id === preset.id);
    if (existingRing) {
      setMobileConfigRingId(existingRing.id);
      setShowAddRingPicker(false);
      toast.success(`${preset.name} is already added.`);
      return;
    }
    const startOfYear = new Date(currentDate.getFullYear(), 0, 0);
    const dayOfYearLocal = Math.floor((currentDate.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
    const currentStage = dayOfYearLocal % preset.stages;
    const nextRing: CycleRing = {
      ...preset,
      id: preset.id,
      currentStage,
      rotation: -currentStage * (360 / preset.stages),
    };
    onRingsChange([...cycleRings, nextRing]);
    setMobileConfigRingId(nextRing.id);
    setShowAddRingPicker(false);
    toast.success(`Added ${preset.name}.`);
  }, [currentDate, cycleRings, mobileAddRingPresetId, onRingsChange]);

  useEffect(() => {
    setShortRowLoading(true);
    if (shortRowLoadingTimerRef.current) {
      clearTimeout(shortRowLoadingTimerRef.current);
    }
    shortRowLoadingTimerRef.current = setTimeout(() => setShortRowLoading(false), 700);
    return () => {
      if (shortRowLoadingTimerRef.current) {
        clearTimeout(shortRowLoadingTimerRef.current);
      }
    };
  }, [activeShortSlide?.key]);

  useEffect(() => {
    setMediumRowLoading(true);
    if (mediumRowLoadingTimerRef.current) {
      clearTimeout(mediumRowLoadingTimerRef.current);
    }
    mediumRowLoadingTimerRef.current = setTimeout(() => setMediumRowLoading(false), 700);
    return () => {
      if (mediumRowLoadingTimerRef.current) {
        clearTimeout(mediumRowLoadingTimerRef.current);
      }
    };
  }, [activeTimelineWidget?.id]);

  useEffect(() => {
    setTallRowLoading(true);
    if (tallRowLoadingTimerRef.current) {
      clearTimeout(tallRowLoadingTimerRef.current);
    }
    tallRowLoadingTimerRef.current = setTimeout(() => setTallRowLoading(false), 700);
    return () => {
      if (tallRowLoadingTimerRef.current) {
        clearTimeout(tallRowLoadingTimerRef.current);
      }
    };
  }, [mainWidgetSlide?.key]);

  useEffect(() => {
    if (waveformSlideTimerRef.current) {
      clearTimeout(waveformSlideTimerRef.current);
    }
    if (!isMediumWaveformActive) {
      setWaveformSlideReady(false);
      return;
    }
    setWaveformSlideReady(false);
    waveformSlideTimerRef.current = setTimeout(() => setWaveformSlideReady(true), 40);
    return () => {
      if (waveformSlideTimerRef.current) {
        clearTimeout(waveformSlideTimerRef.current);
      }
    };
  }, [isMediumWaveformActive, mediumSlideIndex, activeTimelineWidget?.id]);

  const setCalendarMode = useCallback((mode: 'moon' | 'day') => {
    const targetWidgetId = mode === 'moon' ? 'grid' : 'gregorian-grid';
    setPrimaryPanelWidget(targetWidgetId);
    const nextMediumIndex = mediumWidgetSlides.findIndex((slide) => slide.widget.id === targetWidgetId);
    if (nextMediumIndex >= 0) {
      setMediumSlideIndex(nextMediumIndex);
    }
  }, [mediumWidgetSlides, setPrimaryPanelWidget]);

  const setDefaultWidgetForRow = useCallback((row: RowName, widgetId: string) => {
    const nextDefaults: Partial<Record<RowName, string>> = { ...defaultWidgetByRow };
    const previousDefaultWidgetId = defaultWidgetByRow[row];

    if (previousDefaultWidgetId && previousDefaultWidgetId !== widgetId && onWidgetCustomizationChange) {
      onWidgetCustomizationChange(previousDefaultWidgetId, {
        dreamspellDefaultRow: null,
      });
    }

    if (!widgetId) {
      delete nextDefaults[row];
      setDefaultWidgetByRow(nextDefaults);
      if (previousDefaultWidgetId && onWidgetCustomizationChange) {
        onWidgetCustomizationChange(previousDefaultWidgetId, {
          dreamspellDefaultRow: null,
        });
      }
      if (onWidgetCustomizationChange) {
        onWidgetCustomizationChange(DREAMSPELL_LAYOUT_DEFAULTS_KEY, {
          dreamspellRowDefaults: nextDefaults,
        });
      }
      return;
    }

    // Ensure default selection and row visibility stay in sync.
    const targetWidget = allKnownWidgets.find((widget) => widget.id === widgetId)
      ?? selectorWidgets.find((widget) => widget.id === widgetId)
      ?? cycleLayoutWidgets.find((widget) => widget.id === widgetId);
    if (targetWidget && onWidgetCustomizationChange) {
      const currentVisibility = getVisibility(targetWidget);
      onWidgetCustomizationChange(widgetId, {
        showInShort: widgetId === HOME_WIDGET_ID ? false : (row === 'short' ? true : currentVisibility.showShort),
        showInMedium: widgetId === HOME_WIDGET_ID ? false : (row === 'medium' ? true : currentVisibility.showMedium),
        showInTall: row === 'tall' ? true : currentVisibility.showTall,
      });
    }

    nextDefaults[row] = widgetId;
    setDefaultWidgetByRow(nextDefaults);
    if (onWidgetCustomizationChange) {
      onWidgetCustomizationChange(widgetId, {
        dreamspellDefaultRow: row,
      });
      onWidgetCustomizationChange(DREAMSPELL_LAYOUT_DEFAULTS_KEY, {
        dreamspellRowDefaults: nextDefaults,
      });
    }
    if (row === 'short') {
      const index = effectiveShortSlides.findIndex((slide) => slide.widget.id === widgetId);
      if (index >= 0) {
        setShortSlideIndex(index);
      } else {
        onAddWidgetById?.(widgetId);
      }
      return;
    }
    if (row === 'medium') {
      const index = mediumWidgetSlides.findIndex((slide) => slide.widget.id === widgetId);
      if (index >= 0) {
        setMediumSlideIndex(index);
      } else {
        onAddWidgetById?.(widgetId);
      }
      return;
    }
    const index = tallWidgetSlides.findIndex((slide) => slide.widget.id === widgetId);
    if (widgetId === HOME_WIDGET_ID) {
      setIsHomeWidgetActive(true);
      return;
    }
    if (index >= 0) {
      setTallSlideIndex(index);
      onSelectCycleWidget?.(widgetId);
    } else {
      onAddWidgetById?.(widgetId);
    }
  }, [allKnownWidgets, cycleLayoutWidgets, defaultWidgetByRow, effectiveShortSlides, getVisibility, mediumWidgetSlides, onAddWidgetById, onSelectCycleWidget, onWidgetCustomizationChange, selectorWidgets, tallWidgetSlides]);

  const startBottomTabLongPress = useCallback((widgetId: string) => {
    if (bottomTabLongPressTimerRef.current) {
      clearTimeout(bottomTabLongPressTimerRef.current);
    }
    bottomTabLongPressTriggeredRef.current = false;
    bottomTabLongPressTimerRef.current = setTimeout(() => {
      bottomTabLongPressTriggeredRef.current = true;
      setPendingHideWidgetId(widgetId);
      setShowHideWidgetDialog(true);
    }, BOTTOM_TAB_LONG_PRESS_MS);
  }, []);

  const cancelBottomTabLongPress = useCallback(() => {
    if (bottomTabLongPressTimerRef.current) {
      clearTimeout(bottomTabLongPressTimerRef.current);
      bottomTabLongPressTimerRef.current = null;
    }
  }, []);

  const handleBottomTabClick = useCallback((widgetId: string) => {
    if (bottomTabLongPressTriggeredRef.current) {
      bottomTabLongPressTriggeredRef.current = false;
      return;
    }
    if (widgetId === HOME_WIDGET_ID) {
      setIsHomeWidgetActive(true);
      return;
    }
    setIsHomeWidgetActive(false);
    setDefaultWidgetForRow('tall', widgetId);
  }, [setDefaultWidgetForRow]);

  const confirmHideWidgetFromBottomRow = useCallback(() => {
    if (!pendingHideWidgetId) return;
    const widget = allKnownWidgets.find((item) => item.id === pendingHideWidgetId)
      ?? bottomNavWidgets.find((item) => item.id === pendingHideWidgetId)
      ?? selectorWidgets.find((item) => item.id === pendingHideWidgetId);
    if (!widget) {
      setShowHideWidgetDialog(false);
      setPendingHideWidgetId(null);
      return;
    }
    const visibility = getVisibility(widget);
    if (!visibility.showTall) {
      setShowHideWidgetDialog(false);
      setPendingHideWidgetId(null);
      return;
    }
    if (tallWidgetSlides.length <= 1) {
      toast.error('Keep at least one widget visible in the bottom row.');
      return;
    }
    applyCycleWidgetVisibility(widget.id, {
      showInShort: visibility.showShort,
      showInMedium: visibility.showMedium,
      showInTall: false,
    });
    setShowHideWidgetDialog(false);
    setPendingHideWidgetId(null);
  }, [allKnownWidgets, applyCycleWidgetVisibility, bottomNavWidgets, getVisibility, pendingHideWidgetId, selectorWidgets, tallWidgetSlides.length]);

  useEffect(() => {
    const widgetId = defaultWidgetByRow.short;
    if (!widgetId) {
      delete defaultAddRequestedRef.current.short;
      return;
    }
    const nextIndex = effectiveShortSlides.findIndex((slide) => slide.widget.id === widgetId);
    if (nextIndex >= 0) {
      if (nextIndex !== shortSlideIndex) {
        setShortSlideIndex(nextIndex);
      }
      delete defaultAddRequestedRef.current.short;
      return;
    }
    if (onAddWidgetById && defaultAddRequestedRef.current.short !== widgetId) {
      defaultAddRequestedRef.current.short = widgetId;
      onAddWidgetById(widgetId);
    }
  }, [defaultWidgetByRow.short, effectiveShortSlides, onAddWidgetById, shortSlideIndex]);

  useEffect(() => {
    const widgetId = defaultWidgetByRow.medium;
    if (!widgetId) {
      delete defaultAddRequestedRef.current.medium;
      return;
    }
    const nextIndex = mediumWidgetSlides.findIndex((slide) => slide.widget.id === widgetId);
    if (nextIndex >= 0) {
      if (nextIndex !== mediumSlideIndex) {
        setMediumSlideIndex(nextIndex);
      }
      delete defaultAddRequestedRef.current.medium;
      return;
    }
    if (onAddWidgetById && defaultAddRequestedRef.current.medium !== widgetId) {
      defaultAddRequestedRef.current.medium = widgetId;
      onAddWidgetById(widgetId);
    }
  }, [defaultWidgetByRow.medium, mediumSlideIndex, mediumWidgetSlides, onAddWidgetById]);

  useEffect(() => {
    const widgetId = defaultWidgetByRow.tall;
    if (!widgetId) {
      delete defaultAddRequestedRef.current.tall;
      return;
    }
    if (widgetId === HOME_WIDGET_ID) {
      setIsHomeWidgetActive(true);
      delete defaultAddRequestedRef.current.tall;
      lastAppliedTallDefaultWidgetIdRef.current = widgetId;
      return;
    }
    const nextIndex = tallWidgetSlides.findIndex((slide) => slide.widget.id === widgetId);
    if (nextIndex < 0) {
      if (onAddWidgetById && defaultAddRequestedRef.current.tall !== widgetId) {
        defaultAddRequestedRef.current.tall = widgetId;
        onAddWidgetById(widgetId);
      }
      return;
    }
    delete defaultAddRequestedRef.current.tall;
    if (lastAppliedTallDefaultWidgetIdRef.current === widgetId) return;
    if (nextIndex >= 0) {
      lastAppliedTallDefaultWidgetIdRef.current = widgetId;
      if (nextIndex !== tallSlideIndexRef.current) {
        setTallSlideIndex(nextIndex);
      }
      if (selectedCycleWidgetId !== widgetId) {
        onSelectCycleWidget?.(widgetId);
      }
    }
  }, [defaultWidgetByRow.tall, onAddWidgetById, onSelectCycleWidget, selectedCycleWidgetId, tallWidgetSlides]);

  const timelineDays = useMemo(() => {
    const start = new Date(currentDate);
    start.setDate(currentDate.getDate() - 6);
    return Array.from({ length: 14 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return date;
    });
  }, [currentDate]);

  const dailyDecodeText = useMemo(() => {
    const messages = [
      `Your signal is strong today. ${monthInfo?.name ?? 'This moon'} wants movement, not hesitation.`,
      `Follow the first clean impulse. Overthinking will flatten the charge that is already available.`,
      `Protect your attention. Small distractions will cost more than usual on a ${moonPhaseName.toLowerCase()}.`,
      `Something simple wants completion today. Finish it before you open anything new.`,
    ];
    return messages[(day13.day - 1) % messages.length];
  }, [day13.day, monthInfo?.name, moonPhaseName]);

  const mainPanelContent = useMemo(() => {
    const panelId = mainWidgetSlide?.widget.id ?? 'ai-day-summary';
    if (panelId === 'gregorian-grid') {
      return (
        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/45">Gregorian Calendar</div>
            <div className="mt-2 text-3xl font-semibold text-white">{format(currentDate, 'MMMM yyyy')}</div>
          </div>
          <div className="grid grid-cols-7 gap-2 text-center text-xs text-white/55">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((label) => (
              <div key={label} className="uppercase tracking-[0.18em]">{label}</div>
            ))}
            {Array.from({ length: 14 }, (_, index) => {
              const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), index + 1);
              const isToday = date.getDate() === currentDate.getDate();
              return (
                <div
                  key={`calendar-day-${index + 1}`}
                  className={cn(
                    'flex h-10 items-center justify-center rounded-full border text-sm',
                    isToday ? 'border-white/80 bg-white text-slate-950' : 'border-white/10 bg-white/5 text-white/72',
                  )}
                >
                  {index + 1}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    if (panelId === 'cycles') {
      return (
        <div className="space-y-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/45">Cycle Field</div>
          <div className="flex items-center justify-center py-2">
            <div className="relative h-52 w-52 rounded-full border border-fuchsia-300/30">
              <div className="absolute inset-4 rounded-full border border-cyan-300/30" />
              <div className="absolute inset-10 rounded-full border border-amber-200/30" />
              <div className="absolute inset-0 flex items-center justify-center text-center">
                <div>
                  <div className="text-4xl font-semibold text-white">{day13.day}</div>
                  <div className="mt-1 text-sm text-white/62">{monthInfo?.name} Moon</div>
                </div>
              </div>
            </div>
          </div>
          <div className="text-sm leading-7 text-white/74">
            Moon {day13.month} of 13. Year {day13.year}. The field is stable enough to choose one thing and stay with it.
          </div>
        </div>
      );
    }
    if (panelId === 'timeline') {
      return (
        <div className="space-y-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/45">Timeline</div>
          <div className="rounded-[22px] border border-white/10 bg-white/5 p-4">
            <div className="text-2xl font-semibold text-white">{format(currentDate, 'EEEE')}</div>
            <div className="mt-1 text-white/62">{format(currentDate, 'MMMM d, yyyy • HH:mm:ss')}</div>
          </div>
          <div className="text-sm leading-7 text-white/74">
            The current line is active. Stay with what is already unfolding instead of forcing a new branch.
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/45">Your Daily Decode</div>
          <div className="mt-2 text-[clamp(1.8rem,6vw,2.6rem)] font-semibold leading-tight text-white">
            Cycle Day {Math.max(1, ((day13.month - 1) * 28) + day13.day)} of 364
          </div>
        </div>
        <div className="rounded-[24px] border border-white/10 bg-white/5 p-5 text-lg leading-8 text-white/82 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          {dailyDecodeText}
        </div>
      </div>
    );
  }, [currentDate, dailyDecodeText, day13.day, day13.month, day13.year, mainWidgetSlide?.widget.id, monthInfo?.name]);

  const galacticDayNumber = useMemo(() => {
    if (day13.isOutOfTime) return 'Day Out of Time';
    const galacticOrdinal = day13.month * 28 + day13.day - 28;
    return `Day ${galacticOrdinal} of 364`;
  }, [day13]);

  const dayOfYear = useMemo(() => {
    const start = new Date(currentDate.getFullYear(), 0, 0);
    const diff = currentDate.getTime() - start.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }, [currentDate]);

  // Animation lock refs for short slider (matching tall slider pattern)
  const shortSlideAnimatingRef = useRef(false);
  const shortSlideUnlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastShortSlideKeyRef = useRef<string | null>(null);
  const tallDefaultInitializedRef = useRef(false);
  
  const mediumSlideAnimatingRef = useRef(false);
  const mediumSlideUnlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tallSlideAnimatingRef = useRef(false);
  const tallSlideUnlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tallSlideIndexRef = useRef(0);
  const lastAppliedTallDefaultWidgetIdRef = useRef<string | null>(null);
  const loveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mediumSwipeStartXRef = useRef<number | null>(null);
  const tallSwipeStartXRef = useRef<number | null>(null);
  const shortSwipeStartXRef = useRef<number | null>(null);
  
  const goToShortSlide = useCallback((next: number) => {
    if (shortSlideAnimatingRef.current) return;
    shortSlideAnimatingRef.current = true;
    if (shortSlideUnlockTimerRef.current) {
      clearTimeout(shortSlideUnlockTimerRef.current);
      shortSlideUnlockTimerRef.current = null;
    }

    const slideCount = effectiveShortSlides.length;
    if (slideCount <= 0) return;
    
    // Wrap with proper bounds checking
    const wrapped = ((next % slideCount) + slideCount) % slideCount;
    setShortSlideIndex(wrapped);

    // Unlock after the transition completes
    shortSlideUnlockTimerRef.current = setTimeout(() => {
      shortSlideAnimatingRef.current = false;
    }, 520);
  }, [effectiveShortSlides.length]);
  const goToMediumSlide = useCallback((next: number) => {
    if (mediumSlideAnimatingRef.current) return;
    mediumSlideAnimatingRef.current = true;
    if (mediumSlideUnlockTimerRef.current) {
      clearTimeout(mediumSlideUnlockTimerRef.current);
      mediumSlideUnlockTimerRef.current = null;
    }
    const slideCount = mediumWidgetSlides.length;
    if (slideCount <= 0) return;
    const wrapped = ((next % slideCount) + slideCount) % slideCount;
    setMediumSlideIndex(wrapped);
    mediumSlideUnlockTimerRef.current = setTimeout(() => {
      mediumSlideAnimatingRef.current = false;
    }, 520);
  }, [mediumWidgetSlides.length]);
  const goToTallSlide = useCallback((next: number) => {
    if (tallSlideAnimatingRef.current) return;
    tallSlideAnimatingRef.current = true;
    if (tallSlideUnlockTimerRef.current) {
      clearTimeout(tallSlideUnlockTimerRef.current);
      tallSlideUnlockTimerRef.current = null;
    }

    const slideCount = tallWidgetSlides.length;
    if (slideCount <= 0) return;
    
    // Wrap with proper bounds checking
    const wrapped = ((next % slideCount) + slideCount) % slideCount;
    setIsHomeWidgetActive(false);
    setTallSlideIndex(wrapped);
    const nextSlide = tallWidgetSlides[wrapped];
    if (nextSlide?.widget.id && nextSlide.widget.id !== selectedCycleWidgetId) {
      onSelectCycleWidget?.(nextSlide.widget.id);
    }

    // Unlock after the transition completes
    tallSlideUnlockTimerRef.current = setTimeout(() => {
      tallSlideAnimatingRef.current = false;
    }, 520);
  }, [onSelectCycleWidget, selectedCycleWidgetId, tallWidgetSlides]);
  const handleSwipeStart = useCallback((ref: React.MutableRefObject<number | null>, clientX: number) => {
    ref.current = clientX;
  }, []);
  const handleSwipeEnd = useCallback((
    ref: React.MutableRefObject<number | null>,
    clientX: number,
    onPrev: () => void,
    onNext: () => void,
  ) => {
    if (ref.current == null) return;
    const deltaX = clientX - ref.current;
    ref.current = null;
    if (Math.abs(deltaX) < 36) return;
    if (deltaX < 0) {
      onNext();
      return;
    }
    onPrev();
  }, []);
  const handleSyncNow = useCallback(() => {
    onSyncToNow?.();
    setShowLove(true);
    if (loveTimeoutRef.current) {
      clearTimeout(loveTimeoutRef.current);
    }
    loveTimeoutRef.current = setTimeout(() => setShowLove(false), 900);
  }, [onSyncToNow]);

  const applyDateTimeSelection = useCallback((dateValue: string, timeValue: string, successPrefix = 'Set to') => {
    const selectedDateTime = new Date(`${dateValue}T${timeValue}:00`);
    if (Number.isNaN(selectedDateTime.getTime())) {
      toast.error('Invalid date/time selection.');
      return;
    }

    const selectedDay13 = gregorianToDay13(selectedDateTime);
    const selectedDayOfYear = selectedDay13.isOutOfTime
      ? 364
      : Math.max(1, ((selectedDay13.month - 1) * 28) + selectedDay13.day);
    const selectedFractionOfDay = (
      (selectedDateTime.getHours() * 60 * 60) +
      (selectedDateTime.getMinutes() * 60) +
      selectedDateTime.getSeconds()
    ) / (24 * 60 * 60);

    onRotationChange?.(selectedDayOfYear, selectedFractionOfDay);
    toast.success(`${successPrefix} ${dateValue} ${timeValue}`);
  }, [onRotationChange]);

  const handleApplySelectedDateTime = useCallback(() => {
    if (!manualDateInput || !manualTimeInput) {
      toast.error('Select both a date and time.');
      return;
    }
    applyDateTimeSelection(manualDateInput, manualTimeInput);
  }, [applyDateTimeSelection, manualDateInput, manualTimeInput]);

  const handleSyncToNewMoon = useCallback(() => {
    const newMoonDate = '2026-05-15';
    const newMoonTime = '12:00';
    setManualDateInput(newMoonDate);
    setManualTimeInput(newMoonTime);
    setDateStripMode('gregorian');
    applyDateTimeSelection(newMoonDate, newMoonTime, 'Synced to new moon');
  }, [applyDateTimeSelection]);

  useEffect(() => {
    // Don't reset while the user is typing in the fields
    if (manualInputFocusedRef.current) return;
    setManualDateInput(toDateInputValue(currentDate));
    setManualTimeInput(toTimeInputValue(currentDate));
  }, [currentDate]);

  useEffect(() => {
    if (!showMobileConfigDialog) return;
    if (!mobileConfigWidgetId) {
      const preferredWidgetId = selectedCycleWidgetId
        ?? defaultWidgetByRow.tall
        ?? selectorWidgets[0]?.id
        ?? '';
      if (preferredWidgetId) {
        setMobileConfigWidgetId(preferredWidgetId);
      }
    }
  }, [defaultWidgetByRow.tall, mobileConfigWidgetId, selectedCycleWidgetId, selectorWidgets, showMobileConfigDialog]);

  useEffect(() => {
    if (!showMobileConfigDialog) return;
    if (!mobileConfigRingId) {
      const nextRingId = cycleRings[0]?.id;
      if (nextRingId) {
        setMobileConfigRingId(nextRingId);
      }
      return;
    }
    if (!cycleRings.some((ring) => ring.id === mobileConfigRingId)) {
      setMobileConfigRingId(cycleRings[0]?.id ?? '');
    }
  }, [cycleRings, mobileConfigRingId, showMobileConfigDialog]);

  useEffect(() => {
    if (!selectedMobileRing) {
      setMobileStageLabelsDraft('');
      setMobileStageIconsInput('');
      return;
    }
    const labels = Array.isArray(selectedMobileRing.labels) ? selectedMobileRing.labels : [];
    setMobileStageLabelsDraft(labels.join('\n'));
  }, [selectedMobileRing]);
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => setIsMobile(window.innerWidth < 768);
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  useEffect(() => {
    const activeSlide = effectiveShortSlides[shortSlideIndex];
    if (activeSlide?.key) {
      lastShortSlideKeyRef.current = activeSlide.key;
    }
  }, [effectiveShortSlides, shortSlideIndex]);

  useEffect(() => {
    if (effectiveShortSlides.length === 0) return;

    const lastKey = lastShortSlideKeyRef.current;
    if (lastKey) {
      const nextIndex = effectiveShortSlides.findIndex((slide) => slide.key === lastKey);
      if (nextIndex !== -1 && nextIndex !== shortSlideIndex) {
        setShortSlideIndex(nextIndex);
        return;
      }
    }

    if (shortSlideIndex > maxShortSlide) {
      setShortSlideIndex(Math.max(0, maxShortSlide));
    }
  }, [effectiveShortSlides, maxShortSlide, shortSlideIndex]);

  useEffect(() => {
    if (mediumWidgetSlides.length === 0) return;
    if (mediumSlideIndex > maxMediumSlide) {
      setMediumSlideIndex(Math.max(0, maxMediumSlide));
    }
  }, [maxMediumSlide, mediumSlideIndex, mediumWidgetSlides.length]);

  // Keep a ref so the sync effect can compare without being re-triggered by
  // every local tab tap (which was causing the effect to immediately override
  // setPrimaryPanelWidget's setTallSlideIndex call back to the old value).
  useEffect(() => { tallSlideIndexRef.current = tallSlideIndex; }, [tallSlideIndex]);

  useEffect(() => {
    if (tallWidgetSlides.length === 0) return;

    if (!tallDefaultInitializedRef.current) {
      const initialPreferredWidgetIds = [
        defaultWidgetByRow.tall,
        selectedCycleWidgetId,
        'ai-day-summary',
        'gregorian-grid',
        'timeline',
        'cycles',
        'grid',
      ]
        .filter((widgetId): widgetId is string => typeof widgetId === 'string' && widgetId.length > 0);
      const defaultTallIndex = initialPreferredWidgetIds
        .map((widgetId) => tallWidgetSlides.findIndex((slide) => slide.widget.id === widgetId))
        .find((index) => index >= 0) ?? -1;
      if (defaultTallIndex >= 0) {
        tallDefaultInitializedRef.current = true;
        const defaultWidgetId = tallWidgetSlides[defaultTallIndex]?.widget.id;
        if (defaultTallIndex !== tallSlideIndexRef.current) {
          setTallSlideIndex(defaultTallIndex);
        }
        if (defaultWidgetId && selectedCycleWidgetId !== defaultWidgetId) {
          onSelectCycleWidget?.(defaultWidgetId);
        }
        return;
      }
      tallDefaultInitializedRef.current = true;
    }

    const preferredWidgetIds = [
      selectedCycleWidgetId,
      'ai-day-summary',
      'gregorian-grid',
      'timeline',
      'agenda',
      'reminders',
      'cycles',
      'grid',
    ].filter((value): value is string => typeof value === 'string' && value.length > 0);

    const nextIndex = preferredWidgetIds
      .map((widgetId) => tallWidgetSlides.findIndex((slide) => slide.widget.id === widgetId))
      .find((index) => index >= 0);

    if (typeof nextIndex === 'number') {
      if (nextIndex !== tallSlideIndexRef.current) {
        setTallSlideIndex(nextIndex);
      }
      const nextWidgetId = tallWidgetSlides[nextIndex]?.widget.id;
      if (nextWidgetId && nextWidgetId !== selectedCycleWidgetId) {
        onSelectCycleWidget?.(nextWidgetId);
      }
      return;
    }

    if (tallSlideIndexRef.current > maxTallSlide) {
      setTallSlideIndex(Math.max(0, maxTallSlide));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultWidgetByRow.tall, maxTallSlide, onSelectCycleWidget, selectedCycleWidgetId, tallWidgetSlides]);

  const renderWidgetContent = useCallback((
    widget: WidgetConfig | null | undefined,
    variant: 'short' | 'medium' | 'tall',
  ) => {
    if (!widget) return null;
    const source = variant === 'short'
      ? (widget.shortComponent ?? widget.component)
      : variant === 'medium'
        ? (widget.shortComponentTall ?? widget.shortComponent ?? widget.component)
        : widget.component;
    const widgetContent = resolveComponent(source);
    const componentElement = isValidElement(widgetContent) ? (widgetContent as React.ReactElement) : null;
    const widgetSize = variant === 'short' ? shortWidgetSize : variant === 'medium' ? mediumWidgetSize : tallWidgetSize;
    return componentElement
      ? cloneElement(componentElement, { widgetSize, ...componentElement.props })
      : widgetContent;
  }, [mediumWidgetSize, shortWidgetSize, tallWidgetSize]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (loveTimeoutRef.current) {
        clearTimeout(loveTimeoutRef.current);
      }
      if (tallSlideUnlockTimerRef.current) {
        clearTimeout(tallSlideUnlockTimerRef.current);
      }
      if (shortSlideUnlockTimerRef.current) {
        clearTimeout(shortSlideUnlockTimerRef.current);
      }
      if (bottomTabLongPressTimerRef.current) {
        clearTimeout(bottomTabLongPressTimerRef.current);
      }
    };
  }, []);

  // Measure header (for reference only, not for hard height constraints)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Just ensure the ref is set, we'll let flex handle heights
    return () => {
      // cleanup if needed
    };
  }, []);

  const getStageFromRotation = useCallback((rotation: number, stages: number) => {
    const segmentAngle = 360 / Math.max(1, stages);
    const normalizedRotation = ((-rotation % 360) + 360) % 360;
    return Math.floor(normalizedRotation / segmentAngle + 1e-6) % Math.max(1, stages);
  }, []);

  const daysRing = useMemo(() => cycleRings.find((ring) => ring.id === 'days'), [cycleRings]);
  const moonsRing = useMemo(() => cycleRings.find((ring) => ring.id === 'moons'), [cycleRings]);

  const selectedDayInMoon = useMemo(() => {
    if (daysRing && Number.isFinite(daysRing.rotation)) {
      return getStageFromRotation(daysRing.rotation, daysRing.stages || 28) + 1;
    }
    return Math.max(1, Math.min(28, day13.day || 1));
  }, [day13.day, daysRing, getStageFromRotation]);

  const selectedMoonInYear = useMemo(() => {
    if (moonsRing && Number.isFinite(moonsRing.rotation)) {
      return getStageFromRotation(moonsRing.rotation, moonsRing.stages || 13) + 1;
    }
    return Math.max(1, Math.min(13, day13.month || 1));
  }, [day13.month, getStageFromRotation, moonsRing]);

  const cycleDayOfYear = Math.max(1, ((selectedMoonInYear - 1) * 28) + selectedDayInMoon);
  const periodCountdown = Math.max(1, 29 - day13.day);
  const DATE_STRIP_ITEM_WIDTH = 52;
  const GREGORIAN_STRIP_DAYS = 61;
  const GREGORIAN_CENTER_INDEX = Math.floor(GREGORIAN_STRIP_DAYS / 2);
  const isGregorianDateStrip = dateStripMode === 'gregorian';
  const dateStripItemCount = isGregorianDateStrip ? GREGORIAN_STRIP_DAYS : 28;
  const dateStripSelectedIndex = isGregorianDateStrip ? GREGORIAN_CENTER_INDEX : selectedDayInMoon - 1;

  const scrollDateStripToIndex = useCallback((index: number, behavior: ScrollBehavior = 'auto') => {
    const rail = dateStripRef.current;
    if (!rail) return;
    // Cancel any pending scroll-end emission triggered by user scroll
    if (dateStripScrollEndTimerRef.current) {
      clearTimeout(dateStripScrollEndTimerRef.current);
      dateStripScrollEndTimerRef.current = null;
    }
    dateStripPendingEmitRef.current = null;
    const clamped = Math.max(0, Math.min(dateStripItemCount - 1, index));
    const targetLeft = clamped * DATE_STRIP_ITEM_WIDTH;
    dateStripSyncingRef.current = true;
    rail.scrollTo({ left: targetLeft, behavior });
    if (dateStripIdleTimerRef.current) {
      clearTimeout(dateStripIdleTimerRef.current);
    }
    dateStripIdleTimerRef.current = setTimeout(() => {
      dateStripSyncingRef.current = false;
    }, behavior === 'smooth' ? 260 : 80);
  }, [dateStripItemCount]);

  useEffect(() => {
    scrollDateStripToIndex(dateStripSelectedIndex, 'auto');
    dateStripLastEmittedDayRef.current = dateStripSelectedIndex;
  }, [dateStripSelectedIndex, scrollDateStripToIndex]);

  useEffect(() => {
    return () => {
      if (dateStripIdleTimerRef.current) {
        clearTimeout(dateStripIdleTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const el = dateStripRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      el.scrollLeft += delta;
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount once — dateStripRef is stable

  const handleDateStripPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dateStripDragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startScrollLeft: e.currentTarget.scrollLeft,
    };
  }, []);

  const handleDateStripPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dateStripDragRef.current || e.pointerType !== 'mouse') return;
    e.currentTarget.scrollLeft =
      dateStripDragRef.current.startScrollLeft + (dateStripDragRef.current.startX - e.clientX);
  }, []);

  const handleDateStripPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dateStripDragRef.current || e.pointerType !== 'mouse') return;
    dateStripDragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);

  const handleDateStripScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if (dateStripSyncingRef.current) return;
    if (!onRotationChange) return;
    const rail = event.currentTarget;
    const nextIndex = Math.max(0, Math.min(dateStripItemCount - 1, Math.round(rail.scrollLeft / DATE_STRIP_ITEM_WIDTH)));
    if (nextIndex === dateStripLastEmittedDayRef.current) return;
    dateStripLastEmittedDayRef.current = nextIndex;
    const nextDayOfYear = isGregorianDateStrip
      ? Math.max(1, Math.min(365, cycleDayOfYear + (nextIndex - GREGORIAN_CENTER_INDEX)))
      : Math.max(1, Math.min(365, ((selectedMoonInYear - 1) * 28) + (nextIndex + 1)));
    // Debounce: store latest and emit once 100ms after scrolling stops
    dateStripPendingEmitRef.current = { day: nextDayOfYear, fraction: 0 };
    if (dateStripScrollEndTimerRef.current) {
      clearTimeout(dateStripScrollEndTimerRef.current);
    }
    dateStripScrollEndTimerRef.current = setTimeout(() => {
      dateStripScrollEndTimerRef.current = null;
      const pending = dateStripPendingEmitRef.current;
      dateStripPendingEmitRef.current = null;
      if (pending && onRotationChange) {
        onRotationChange(pending.day, pending.fraction);
      }
    }, 100);
  }, [GREGORIAN_CENTER_INDEX, cycleDayOfYear, dateStripItemCount, isGregorianDateStrip, onRotationChange, selectedMoonInYear]);

  return (
    <div
      className="relative w-full h-full overflow-hidden flex flex-col min-h-0"
      style={{
        background: 'linear-gradient(180deg, #180535 0%, #13042d 38%, #100326 68%, #0c021e 100%)',
      }}
    >
      {/* Layered cosmic background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Deep teal base gradient */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 120% 80% at 50% 0%, rgba(92,51,175,0.34) 0%, transparent 55%),
              radial-gradient(ellipse 100% 60% at 80% 100%, rgba(40,20,88,0.5) 0%, transparent 44%),
              radial-gradient(ellipse 80% 50% at 20% 80%, rgba(31,14,74,0.45) 0%, transparent 48%)
            `,
          }}
        />
        
        {/* Golden accent glows */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(circle 300px at 75% 15%, rgba(114,225,215,0.12) 0%, transparent 60%),
              radial-gradient(circle 250px at 25% 85%, rgba(255,95,152,0.12) 0%, transparent 50%),
              radial-gradient(circle 400px at 50% 50%, rgba(120,72,255,0.1) 0%, transparent 70%)
            `,
          }}
        />
        
        {/* Animated starfield - large stars */}
        {[...Array(30)].map((_, i) => (
          <div
            key={`star-lg-${i}`}
            className="absolute rounded-full animate-pulse"
            style={{
              width: 3 + Math.random() * 2,
              height: 3 + Math.random() * 2,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: `radial-gradient(circle, ${i % 3 === 0 ? '#f4d03f' : i % 3 === 1 ? '#d4a574' : '#a8d4cf'} 0%, transparent 70%)`,
              boxShadow: `0 0 ${4 + Math.random() * 6}px ${i % 3 === 0 ? 'rgba(244,208,63,0.6)' : 'rgba(168,212,207,0.4)'}`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${2 + Math.random() * 4}s`,
            }}
          />
        ))}
        
        {/* Small twinkling stars */}
        {[...Array(80)].map((_, i) => (
          <div
            key={`star-sm-${i}`}
            className="absolute rounded-full"
            style={{
              width: 1 + Math.random(),
              height: 1 + Math.random(),
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: i % 2 === 0 ? '#c9a869' : '#7eb8b5',
              opacity: 0.3 + Math.random() * 0.5,
            }}
          />
        ))}
        
        <div className="absolute top-4 left-4 opacity-30">
          <CelestialSymbol type="star" className="w-3 h-3" />
        </div>
        <div className="absolute top-4 right-6 opacity-20">
          <CelestialSymbol type="moon" className="w-4 h-4" />
        </div>
      </div>

      {/* Love animation */}
      {showLove && (
        <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
          <div className="animate-ping">
            <Heart className="w-24 h-24 text-amber-400 fill-amber-400/50" />
          </div>
        </div>
      )}

      {/* Fixed Header Area - hidden for guests */}
      <div ref={headerRef} className="relative z-10 flex-shrink-0 px-4" style={{ paddingTop: isAdmin ? 'max(0.75rem, env(safe-area-inset-top, 0px))' : '0px', paddingBottom: isAdmin ? '0.25rem' : '0px' }}>
        {isAdmin && <div className="flex items-center gap-2">
          <span className="text-sm font-semibold tracking-[0.42em] text-white/90 uppercase leading-none">Thirteen</span>
          {(canShowMoonCalendar || canShowDayCalendar || cycleLayoutWidgets.length > 0) ? (
            <div className="ml-2 inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/28 p-1 shadow-[0_8px_24px_rgba(0,0,0,0.28)] backdrop-blur-md">
              {canShowMoonCalendar ? (
                <button
                  type="button"
                  onClick={() => setCalendarMode('moon')}
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
                    activeMainWidgetId === 'grid'
                      ? "bg-white text-slate-950"
                      : "text-white/72 hover:bg-white/10 hover:text-white"
                  )}
                  aria-label="Show moon calendar"
                  title="Moon calendar"
                >
                  <Moon className="h-4 w-4" />
                </button>
              ) : null}
              {canShowDayCalendar ? (
                <button
                  type="button"
                  onClick={() => setCalendarMode('day')}
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
                    activeMainWidgetId === 'gregorian-grid'
                      ? "bg-white text-slate-950"
                      : "text-white/72 hover:bg-white/10 hover:text-white"
                  )}
                  aria-label="Show day calendar"
                  title="Day calendar"
                >
                  <Sun className="h-4 w-4" />
                </button>
              ) : null}
              {cycleLayoutWidgets.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setShowCycleLayoutDialog(true)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-white/72 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label="Open cycle row selector"
                  title="Cycle row selector"
                >
                  <Settings2 className="h-4 w-4" />
                </button>
              ) : null}
              {showPrivilegedFixedSyncUi ? (
                <button
                  type="button"
                  onClick={() => {
                    if (isMobile) {
                      setShowMobileConfigDialog(true);
                      return;
                    }
                    setShowCycleLayoutDialog(true);
                  }}
                  className="inline-flex h-8 items-center justify-center rounded-full px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/72 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label="Open cycle settings"
                  title="Cycle settings"
                >
                  Advanced
                </button>
              ) : null}
            </div>
          ) : null}
        </div>}

      </div>

      <div ref={gridRef} className="relative flex-1 min-h-0 overflow-hidden px-2.5">
        <div
          className={cn(
            "relative mx-auto grid h-full w-full gap-0 pb-0",
            isMediumWaveformActive || isTopCyclesActive ? "max-w-none" : "max-w-xl",
          )}
          style={{ gridTemplateRows: topGridTemplateRows }}
        >
            <section className="relative h-full min-h-0 overflow-hidden px-0 pt-0">
              <div className="pointer-events-none absolute inset-0 opacity-80" style={{ background: 'radial-gradient(circle at 50% 12%, rgba(132,103,255,0.18), transparent 34%), radial-gradient(circle at 50% 100%, rgba(255,72,124,0.1), transparent 28%)' }} />
              <div className="pointer-events-none absolute inset-0">
                {[...Array(34)].map((_, index) => (
                  <div
                    key={`dreamspell-star-${index}`}
                    className="absolute rounded-full bg-white/60"
                    style={{
                      width: `${1 + (index % 3)}px`,
                      height: `${1 + (index % 3)}px`,
                      left: `${(index * 17) % 100}%`,
                      top: `${(index * 23) % 100}%`,
                      opacity: 0.25 + ((index % 5) * 0.08),
                    }}
                  />
                ))}
              </div>

              <div
                className={cn(
                  "group relative mx-auto flex h-full min-h-0 w-full max-w-xl flex-col items-stretch rounded-[12px] pt-0",
                  lockedTopWidget?.id === 'waveform' ? "bg-transparent" : "bg-black/22",
                  lockedTopWidget?.id !== 'cycles' && !isMediumWaveformActive && 'items-center justify-center'
                )}
              >
                {lockedTopWidget?.id === 'cycles' ? (
                  <div className={cn("relative h-full w-full min-h-0 overflow-visible", shortRowLoading && "animate-pulse") }>
                    {isNowLocked && (
                      <div className="absolute top-1.5 right-1.5 z-30 pointer-events-none">
                        <span className="rounded px-1.5 py-0.5 text-[9px] font-bold tracking-wider bg-emerald-500/25 text-emerald-300 border border-emerald-400/30">
                          NOW
                        </span>
                      </div>
                    )}
                    <MultiRing
                        rings={cycleRings}
                        hiddenRings={hiddenRings}
                      onRingsChange={onRingsChange}
                      onToggleRing={onToggleRing}
                        hideToolbar
                        constrainToContainer
                        hideRail={widgetCustomizations?.['cycles']?.cycleRailHidden ?? false}
                        railOrientation="horizontal"
                        ringThemeLayout={{ visibleRingCount: topCycleVisibleCount }}
                        displayMode={cycleDisplayMode}
                        gregorianDate={currentDate}
                        onSyncToNow={onSyncToNow}
                        onRotationChange={onRotationChange}
                        maxRings={Math.max(1, cycleRings.length)}
                        />
                  </div>
                ) : lockedTopWidget ? (
                  <div className="flex h-full w-full min-h-0 items-center justify-center px-0 pb-0">
                    <div className={cn("h-full w-full overflow-visible", shortRowLoading && "animate-pulse")}>
                      {renderWidgetContent(lockedTopWidget, 'tall')}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-2 flex items-center justify-center gap-3 text-white/55">
                      {[0, 1, 2, 3, 4].map((index) => (
                        <div
                          key={`phase-arc-${index}`}
                          className={cn(
                            'rounded-full border border-cyan-200/20 bg-cyan-300/10',
                            index === 2 ? 'h-5 w-5 shadow-[0_0_18px_rgba(80,220,255,0.45)]' : 'h-3.5 w-3.5'
                          )}
                        />
                      ))}
                    </div>
                    <div className="relative flex h-[min(38vw,290px)] w-[min(38vw,290px)] min-h-[238px] min-w-[238px] max-h-[290px] max-w-[290px] items-center justify-center">
                      <div className="absolute inset-[24px] rounded-full border border-white/8 bg-[radial-gradient(circle,rgba(37,23,87,0.9)_0%,rgba(23,11,58,0.48)_56%,transparent_76%)] shadow-[0_0_90px_rgba(101,77,255,0.18)]" />
                      <div className="absolute inset-0 rounded-full border border-white/8" />
                      <div className="absolute inset-[24px] rounded-full border border-white/8" />
                      {[...Array(28)].map((_, index) => {
                        const angle = (index / 28) * Math.PI * 2 - Math.PI / 2;
                        const radius = 144;
                        const x = Math.cos(angle) * radius;
                        const y = Math.sin(angle) * radius;
                        const isPast = index < day13.day - 1;
                        const isCurrent = index === day13.day - 1;
                        const hue = 202 + (index / 28) * 148;
                        return (
                          <div
                            key={`moon-node-${index}`}
                            className={cn(
                              'absolute left-1/2 top-1/2 rounded-full border transition-all duration-300',
                              isCurrent ? 'h-5 w-5 border-[#f6f1d5] bg-[#f7f0c6] shadow-[0_0_20px_rgba(255,244,176,0.5)]' : 'h-3.5 w-3.5',
                            )}
                            style={{
                              transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
                              background: isCurrent
                                ? '#f7f0c6'
                                : isPast
                                  ? `linear-gradient(135deg, hsla(${hue}, 88%, 68%, 1) 0%, hsla(${Math.min(360, hue + 24)}, 92%, 58%, 1) 100%)`
                                  : 'rgba(255,255,255,0.12)',
                              borderColor: isCurrent ? 'rgba(255,244,176,0.9)' : isPast ? 'transparent' : 'rgba(255,255,255,0.18)',
                              boxShadow: isCurrent ? '0 0 18px rgba(255,244,176,0.45)' : isPast ? `0 0 14px hsla(${hue}, 90%, 60%, 0.35)` : 'none',
                            }}
                          />
                        );
                      })}

                      <div className="relative z-10 flex w-[188px] flex-col items-center gap-2 text-center">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.34em] text-white/55">
                          Period In
                        </div>
                        <div className="text-5xl font-semibold leading-none text-white">
                          {periodCountdown} {periodCountdown === 1 ? 'day' : 'days'}
                        </div>
                        <div className="text-sm font-medium text-white/66">
                          {weekdayName} • {monthInfo?.glyph} {monthInfo?.name}
                        </div>
                        <button
                          type="button"
                          onClick={handleSyncNow}
                          className="mt-1 inline-flex items-center rounded-full border border-violet-200/16 bg-[linear-gradient(180deg,rgba(98,55,215,0.9)_0%,rgba(82,43,185,0.95)_100%)] px-5 py-2 text-sm font-medium text-white shadow-[0_12px_24px_rgba(70,30,180,0.35)]"
                        >
                          Add Period
                        </button>
                        <div className="mt-2 flex w-full max-w-[230px] items-center gap-1.5">
                          <input
                            type="date"
                            value={manualDateInput}
                            onChange={(event) => setManualDateInput(event.target.value)}
                            onFocus={() => { manualInputFocusedRef.current = true; }}
                            onBlur={() => { manualInputFocusedRef.current = false; }}
                            className="h-8 min-w-0 flex-1 rounded-md border border-white/18 bg-black/25 px-2 text-[11px] text-white outline-none focus:border-white/45"
                            aria-label="Select date"
                          />
                          <input
                            type="time"
                            value={manualTimeInput}
                            onChange={(event) => setManualTimeInput(event.target.value)}
                            onFocus={() => { manualInputFocusedRef.current = true; }}
                            onBlur={() => { manualInputFocusedRef.current = false; }}
                            className="h-8 w-[92px] rounded-md border border-white/18 bg-black/25 px-2 text-[11px] text-white outline-none focus:border-white/45"
                            aria-label="Select time"
                          />
                          <button
                            type="button"
                            onClick={handleApplySelectedDateTime}
                            className="h-8 rounded-md border border-cyan-300/30 bg-cyan-400/20 px-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-cyan-100 transition-colors hover:bg-cyan-400/30"
                            aria-label="Set selected date and time"
                          >
                            Set
                          </button>
                        </div>
                        {activeShortSlide?.widget.id !== '__header__' ? (
                          <div className={cn("mt-2 w-full max-w-[220px] overflow-hidden rounded-[18px] border border-white/10 bg-white/[0.05] px-2 py-2 shadow-[0_12px_28px_rgba(0,0,0,0.22)]", shortRowLoading && "animate-pulse")}>
                            {renderWidgetContent(activeShortSlide?.widget, 'short')}
                          </div>
                        ) : (
                          <div className="mt-2 text-[10px] uppercase tracking-[0.24em] text-white/40">
                            {shortSlideCount > 1 ? 'Swipe or tap arrows to change cycle data' : activeShortSlide?.widget.title}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Date scroller strip */}
            <section style={{ overflow: 'hidden' }} className={cn("py-0 transition-all duration-400 ease-in-out", (isTallExpanded || isHomeWidgetActive || activeMainWidgetId === 'year-ring') ? "h-0 min-h-0 max-h-0 opacity-0 pointer-events-none" : "min-h-0 opacity-100")}>
              <div className="relative mx-auto flex h-full w-full max-w-xl items-center overflow-hidden">
                <div
                  ref={dateStripRef}
                  className="h-full w-full overflow-x-auto overflow-y-hidden scrollbar-none snap-x snap-mandatory cursor-grab active:cursor-grabbing"
                  onScroll={handleDateStripScroll}
                  onPointerDown={handleDateStripPointerDown}
                  onPointerMove={handleDateStripPointerMove}
                  onPointerUp={handleDateStripPointerUp}
                  onPointerCancel={handleDateStripPointerUp}
                >
                  <div
                    className="flex h-full items-center"
                    style={{ paddingLeft: `calc(50% - ${DATE_STRIP_ITEM_WIDTH / 2}px)`, paddingRight: `calc(50% - ${DATE_STRIP_ITEM_WIDTH / 2}px)` }}
                  >
                  {Array.from({ length: dateStripItemCount }, (_, i) => {
                    const dayNum = i + 1;
                    const dist = isGregorianDateStrip ? Math.abs(i - GREGORIAN_CENTER_INDEX) : Math.abs(dayNum - selectedDayInMoon);
                    const scale = Math.max(0.75, 1 - dist * 0.05);
                    const opacity = Math.max(0.45, 1 - dist * 0.07);
                    const isActive = isGregorianDateStrip ? i === GREGORIAN_CENTER_INDEX : dayNum === selectedDayInMoon;
                    const isBoundary = !isGregorianDateStrip && (dayNum === 1 || dayNum === 28);
                    const greg = new Date(currentDate);
                    const gregorianOffset = isGregorianDateStrip ? (i - GREGORIAN_CENTER_INDEX) : (dayNum - selectedDayInMoon);
                    greg.setDate(currentDate.getDate() + gregorianOffset);
                    const monthAbbr = greg.toLocaleDateString('en-US', { month: 'short' });
                    const dayOfMonth = greg.getDate();
                    const weekDayAbbr = greg.toLocaleDateString('en-US', { weekday: 'short' });
                    const targetDayOfYear = isGregorianDateStrip
                      ? Math.max(1, Math.min(365, cycleDayOfYear + gregorianOffset))
                      : Math.max(1, Math.min(365, ((selectedMoonInYear - 1) * 28) + dayNum));
                    return (
                      <button
                        key={isGregorianDateStrip ? `g-${i}` : dayNum}
                        type="button"
                        onClick={() => onRotationChange?.(targetDayOfYear, 0)}
                        className="relative flex shrink-0 snap-center flex-col items-center justify-center gap-0.5 transition-none"
                        style={{
                          transform: `scale(${scale})`,
                          opacity,
                          width: `${DATE_STRIP_ITEM_WIDTH}px`,
                        }}
                      >
                        {isBoundary ? (
                          <span
                            className={cn(
                              'absolute left-1/2 top-0 h-1.5 w-7 -translate-x-1/2 rounded-full',
                              dayNum === 1 ? 'bg-cyan-300/80' : 'bg-fuchsia-300/80'
                            )}
                          />
                        ) : null}
                        <span className="text-[10px] font-medium uppercase leading-none text-white/60">
                          {isGregorianDateStrip ? weekDayAbbr : (PLASMA_NAMES[(dayNum - 1) % 7]?.short ?? monthAbbr)}
                        </span>
                        <span className={cn('font-bold leading-none', isActive ? 'text-base text-yellow-200' : 'text-sm text-white')}>{dayOfMonth}</span>
                        <span className={cn('text-[9px] leading-none', isActive ? 'text-yellow-200/70' : isBoundary ? 'text-white/65' : 'text-white/40')}>
                          {isGregorianDateStrip
                            ? monthAbbr
                            : (PLASMA_NAMES[(dayNum - 1) % 7]?.name ?? `d${dayNum}`)}
                        </span>
                      </button>
                    );
                  })}
                  </div>
                </div>
              </div>
            </section>

            <section style={{ overflow: 'hidden' }} className={cn("pt-0 transition-all duration-400 ease-in-out", (isTallExpanded || isHomeWidgetActive || activeMainWidgetId === 'year-ring') ? "h-0 min-h-0 max-h-0 opacity-0 pointer-events-none" : "min-h-0 opacity-100")}>
              <div
                className={cn(
                  "relative mx-auto flex h-full w-full max-w-xl flex-col px-0 py-0",
                )}
              >
                <div
                  className={cn(
                    "group relative flex-1 overflow-hidden rounded-[12px] px-0 py-0",
                    "bg-black/25 border border-white/[0.06]",
                  )}
                >
                  <div className="relative z-10 h-full min-h-0">
                    {activeTimelineWidget ? (
                      <div className={cn(
                        "h-full min-h-0 overflow-hidden rounded-[10px]",
                        mediumRowLoading && "animate-pulse",
                        isMediumWaveformActive && "transform transition-all duration-500 ease-out",
                        isMediumWaveformActive && (waveformSlideReady ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0")
                      )}>
                        {renderWidgetContent(activeTimelineWidget, 'medium')}
                      </div>
                    ) : (
                      renderEmptyRow('Medium', 'Add a medium widget to populate this row.')
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className="min-h-0 px-0.5 pt-0">
              <div
                className={cn(
                  "mx-auto flex h-full min-h-0 w-full max-w-xl flex-col",
                )}
              >
              <div
                className="group relative mt-0 flex-1 overflow-hidden rounded-[18px] bg-[linear-gradient(180deg,rgba(18,8,47,0.28)_0%,rgba(15,10,40,0.08)_100%)]"
              >
                {activeMainWidgetId !== 'grid' && activeMainWidgetId !== 'gregorian-grid' && activeMainWidgetId !== HOME_WIDGET_ID && activeMainWidgetId !== 'year-ring' && (
                  <button
                    type="button"
                    onClick={() => setIsTallExpanded(prev => !prev)}
                    className="absolute top-1.5 right-1.5 z-30 flex h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-black/30 text-white/50 backdrop-blur-sm transition-all hover:bg-white/10 hover:text-white/90"
                    aria-label={isTallExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isTallExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
                  </button>
                )}
                <TallRowContext.Provider value={{ isTallExpanded, toggleTallExpanded: () => setIsTallExpanded(prev => !prev) }}>
                  {isHomeWidgetActive ? (
                    <div className={cn("flex h-full min-h-0 items-center justify-center overflow-hidden", tallRowLoading && "animate-pulse")}>
                      <MesaLogo className="h-full w-auto object-contain" />
                    </div>
                  ) : mainWidgetSlide?.widget ? (
                    <div className={cn("h-full min-h-0 px-1 py-1", tallRowLoading && "animate-pulse")}>
                      {renderWidgetContent(mainWidgetSlide.widget, 'tall')}
                    </div>
                  ) : (
                    <div className="px-6 py-7 text-center text-[1.05rem] leading-8 text-white/82">
                      {dailyDecodeText}
                    </div>
                  )}
                </TallRowContext.Provider>
              </div>
              </div>
            </section>
        </div>

      </div>

      {/* Toolbar — flex sibling, always pinned at bottom */}
      <div className="shrink-0 px-3" style={{ paddingBottom: 'max(0.25rem, env(safe-area-inset-bottom, 0px))' }}>
        <div className="mx-auto w-full max-w-xl rounded-[18px] border border-white/12 bg-[rgba(42,28,78,0.72)] shadow-[0_10px_24px_rgba(0,0,0,0.22)] backdrop-blur-2xl px-1 py-1">
          <div className="flex w-full items-center justify-between">
            {bottomNavWidgets.map((widget) => {
              const isActive = activeMainWidgetId === widget.id;
              const customLabelIcon = widgetCustomizations?.[widget.id]?.widgetLabelIcon;
              const isHome = widget.id === HOME_WIDGET_ID;
              const icon = customLabelIcon
                ? <span className="text-base leading-none">{customLabelIcon}</span>
                : isHome
                  ? <MesaLogo className="h-5 w-5 rounded-full" />
                  : inferWidgetIcon(widget);
              const label = isHome ? 'Home' : (WIDGET_TAB_META[widget.id]?.label ?? widgetCustomizations?.[widget.id]?.title ?? widget.title ?? widget.id);
              return (
                <button
                  key={`dreamspell-nav-${widget.id}`}
                  type="button"
                  onClick={() => handleBottomTabClick(widget.id)}
                  onPointerDown={(event) => {
                    if (isHome || event.button !== 0) return;
                    startBottomTabLongPress(widget.id);
                  }}
                  onPointerUp={isHome ? undefined : cancelBottomTabLongPress}
                  onPointerLeave={isHome ? undefined : cancelBottomTabLongPress}
                  onPointerCancel={isHome ? undefined : cancelBottomTabLongPress}
                  className={cn(
                    'flex flex-1 flex-col items-center justify-center gap-0.5 py-1 px-1 rounded-[14px] transition-all duration-200',
                    isActive ? 'text-white' : 'text-white/45',
                  )}
                  aria-label={label}
                  title={label}
                >
                  <div className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200',
                    isActive ? 'bg-white/15 shadow-[0_0_12px_rgba(255,255,255,0.15)]' : '',
                  )}>
                    {icon}
                  </div>
                  <span className={cn(
                    'text-[9px] font-medium tracking-wide leading-none truncate max-w-[52px]',
                    isActive ? 'text-white' : 'text-white/45',
                  )}>{label}</span>
                </button>
              );
            })}
            {/* Full-page Moon Calendar — route link for all users */}
            <button
              key="dreamspell-nav-moon-calendar"
              type="button"
              onClick={() => navigate('/dashboard/thirteen')}
              className="flex flex-1 flex-col items-center justify-center gap-0.5 py-1 px-1 rounded-[14px] text-white/45 transition-all duration-200 hover:text-white"
              aria-label="Open the full 13-Moon Calendar"
              title="Open the full 13-Moon Calendar"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200">
                <Moon className="h-5 w-5" />
              </div>
              <span className="text-[9px] font-medium tracking-wide leading-none truncate max-w-[52px]">Calendar</span>
            </button>
          </div>
        </div>
      </div>

        <Dialog open={showHideWidgetDialog} onOpenChange={(open) => {
          setShowHideWidgetDialog(open);
          if (!open) {
            setPendingHideWidgetId(null);
            bottomTabLongPressTriggeredRef.current = false;
          }
        }}>
          <DialogContent className="max-w-sm border-amber-400/30 bg-[#0f0822]/95 text-white backdrop-blur-xl">
            <DialogHeader>
              <DialogTitle className="text-amber-100">Hide Widget From Bottom Row?</DialogTitle>
              <DialogDescription className="text-amber-100/70">
                This removes
                {' '}
                {(bottomNavWidgets.find((widget) => widget.id === pendingHideWidgetId)?.title
                  ?? allKnownWidgets.find((widget) => widget.id === pendingHideWidgetId)?.title
                  ?? pendingHideWidgetId
                  ?? 'this widget')}
                {' '}
                from the third row tab bar.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowHideWidgetDialog(false);
                  setPendingHideWidgetId(null);
                }}
                className="rounded-md border border-white/20 px-3 py-1.5 text-sm text-white/80 hover:bg-white/10 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmHideWidgetFromBottomRow}
                className="rounded-md bg-amber-500 px-3 py-1.5 text-sm font-medium text-slate-950 hover:bg-amber-400"
              >
                Hide Widget
              </button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showCycleLayoutDialog} onOpenChange={setShowCycleLayoutDialog}>
          <DialogContent className="max-h-[88vh] w-[calc(100vw-1rem)] max-w-[95vw] overflow-y-auto border-amber-400/30 bg-[#0f0822]/95 p-3 text-white backdrop-blur-xl sm:max-w-2xl sm:p-6">
            <DialogHeader>
              <DialogTitle className="text-amber-100 text-xl">Cycle Row Selector</DialogTitle>
              <DialogDescription className="text-amber-100/70 text-sm sm:text-base">
                Set which widgets appear in top, middle, and bottom rows, then choose row defaults including all widgets visible in the third-row toolbar.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setAllCycleWidgetVisibility(false)}
                className="rounded-md border border-white/20 px-2.5 py-1 text-xs font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                All Off
              </button>
              <button
                type="button"
                onClick={() => setAllCycleWidgetVisibility(true)}
                className="rounded-md border border-emerald-400/40 bg-emerald-500/20 px-2.5 py-1 text-xs font-medium text-emerald-100 transition-colors hover:bg-emerald-500/30"
              >
                All On
              </button>
            </div>

            <div className="space-y-4">
              <div className="overflow-x-hidden rounded-xl border border-white/10">
                <table className="w-full table-fixed text-sm sm:text-base">
                  <colgroup>
                    <col className="w-[52%] sm:w-auto" />
                    <col className="w-[16%]" />
                    <col className="w-[16%]" />
                    <col className="w-[16%]" />
                  </colgroup>
                  <thead className="bg-white/[0.04] text-white/80">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium sm:px-3">Cycle Widget</th>
                      <th className="px-1 py-2 text-center font-medium sm:px-3">
                        <button
                          type="button"
                          onClick={() => toggleCycleWidgetColumn('short')}
                          className="w-full rounded-md px-1 py-1 text-sm transition-colors hover:bg-white/8 hover:text-white sm:px-2 sm:text-base"
                        >
                          {isMobile ? 'Top' : 'Top Row'}
                        </button>
                      </th>
                      <th className="px-1 py-2 text-center font-medium sm:px-3">
                        <button
                          type="button"
                          onClick={() => toggleCycleWidgetColumn('medium')}
                          className="w-full rounded-md px-1 py-1 text-sm transition-colors hover:bg-white/8 hover:text-white sm:px-2 sm:text-base"
                        >
                          {isMobile ? 'Middle' : 'Middle Row'}
                        </button>
                      </th>
                      <th className="px-1 py-2 text-center font-medium sm:px-3">
                        <button
                          type="button"
                          onClick={() => toggleCycleWidgetColumn('tall')}
                          className="w-full rounded-md px-1 py-1 text-sm transition-colors hover:bg-white/8 hover:text-white sm:px-2 sm:text-base"
                        >
                          {isMobile ? 'Bottom' : 'Bottom Row'}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectorWidgets.map((widget) => {
                      const visibility = getVisibility(widget);
                      return (
                        <tr key={`cycle-layout-${widget.id}`} className="border-t border-white/8">
                          <td className="px-2 py-2 text-white/92 sm:px-3">
                            <button
                              type="button"
                              onClick={() => toggleCycleWidgetRow(widget)}
                              className="w-full break-words rounded-md px-1 py-1 text-left leading-tight transition-colors hover:bg-white/8 hover:text-white sm:px-2"
                            >
                              {getWidgetDisplayName(widget)}
                            </button>
                          </td>
                          <td className="px-0.5 py-2 text-center sm:px-3">
                            <Checkbox
                              checked={visibility.showShort}
                              onCheckedChange={(checked) => applyCycleWidgetVisibility(widget.id, {
                                showInShort: checked === true,
                                showInMedium: visibility.showMedium,
                                showInTall: visibility.showTall,
                              })}
                              className={cn(
                                defaultWidgetByRow.short === widget.id &&
                                  'border-emerald-400 data-[state=checked]:bg-emerald-500 data-[state=checked]:text-emerald-950'
                              )}
                              aria-label={`Show ${getWidgetDisplayName(widget)} in top row`}
                            />
                          </td>
                          <td className="px-0.5 py-2 text-center sm:px-3">
                            <Checkbox
                              checked={visibility.showMedium}
                              onCheckedChange={(checked) => applyCycleWidgetVisibility(widget.id, {
                                showInShort: visibility.showShort,
                                showInMedium: checked === true,
                                showInTall: visibility.showTall,
                              })}
                              className={cn(
                                defaultWidgetByRow.medium === widget.id &&
                                  'border-emerald-400 data-[state=checked]:bg-emerald-500 data-[state=checked]:text-emerald-950'
                              )}
                              aria-label={`Show ${getWidgetDisplayName(widget)} in middle row`}
                            />
                          </td>
                          <td className="px-0.5 py-2 text-center sm:px-3">
                            <Checkbox
                              checked={visibility.showTall}
                              onCheckedChange={(checked) => applyCycleWidgetVisibility(widget.id, {
                                showInShort: visibility.showShort,
                                showInMedium: visibility.showMedium,
                                showInTall: checked === true,
                              })}
                              className={cn(
                                defaultWidgetByRow.tall === widget.id &&
                                  'border-emerald-400 data-[state=checked]:bg-emerald-500 data-[state=checked]:text-emerald-950'
                              )}
                              aria-label={`Show ${getWidgetDisplayName(widget)} in bottom row`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <div className="mb-2 text-sm uppercase tracking-[0.12em] text-white/65">Top Row Default</div>
                  <select
                    className="w-full rounded-md border border-white/16 bg-black/35 px-2 py-2 text-base text-white"
                    value={defaultWidgetByRow.short ?? ''}
                    onChange={(event) => setDefaultWidgetForRow('short', event.target.value)}
                    disabled={rowDefaultOptions.short.length === 0}
                  >
                    <option value="">Select widget</option>
                    {rowDefaultOptions.short.map((widget) => (
                      <option key={`default-short-${widget.id}`} value={widget.id}>{widget.title}</option>
                    ))}
                  </select>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <div className="mb-2 text-sm uppercase tracking-[0.12em] text-white/65">Middle Row Default</div>
                  <select
                    className="w-full rounded-md border border-white/16 bg-black/35 px-2 py-2 text-base text-white"
                    value={defaultWidgetByRow.medium ?? ''}
                    onChange={(event) => setDefaultWidgetForRow('medium', event.target.value)}
                    disabled={rowDefaultOptions.medium.length === 0}
                  >
                    <option value="">Select widget</option>
                    {rowDefaultOptions.medium.map((widget) => (
                      <option key={`default-medium-${widget.id}`} value={widget.id}>{widget.title}</option>
                    ))}
                  </select>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <div className="mb-2 text-sm uppercase tracking-[0.12em] text-white/65">Bottom Row Default</div>
                  <select
                    className="w-full rounded-md border border-white/16 bg-black/35 px-2 py-2 text-base text-white"
                    value={defaultWidgetByRow.tall ?? ''}
                    onChange={(event) => setDefaultWidgetForRow('tall', event.target.value)}
                    disabled={rowDefaultOptions.tall.length === 0}
                  >
                    <option value="">Select widget</option>
                    {rowDefaultOptions.tall.map((widget) => (
                      <option key={`default-tall-${widget.id}`} value={widget.id}>{widget.title}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showMobileConfigDialog} onOpenChange={setShowMobileConfigDialog}>
          <DialogContent className="max-h-[90vh] w-[calc(100vw-1rem)] max-w-[95vw] flex flex-col gap-0 border-amber-400/30 bg-[#0f0822]/95 p-0 text-white backdrop-blur-xl sm:max-w-md">
            {/* Fixed header */}
            <div className="shrink-0 px-4 pt-4 pb-2">
              <DialogHeader>
                <DialogTitle className="text-amber-100 text-lg">Configure</DialogTitle>
              </DialogHeader>
              {/* Widget selector always visible at top */}
              <select
                className="mt-2 w-full rounded-md border border-white/16 bg-black/35 px-2 py-2 text-base text-white"
                value={mobileConfigWidgetId}
                onChange={(event) => {
                  const nextWidgetId = event.target.value;
                  setMobileConfigWidgetId(nextWidgetId);
                  if (nextWidgetId) onSelectCycleWidget?.(nextWidgetId);
                }}
              >
                <option value="">— Select widget —</option>
                {selectorWidgets.map((widget) => (
                  <option key={`mobile-config-widget-${widget.id}`} value={widget.id}>{getWidgetDisplayName(widget)}</option>
                ))}
              </select>
            </div>

            {/* Unified widget + rings config */}
            <div className="flex flex-col flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-3">
              {mobileConfigWidget && (
                <div key={`mobile-widget-form-${mobileConfigWidget.id}`} className="space-y-3">
                  {/* Widget Label */}
                  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-2">
                    <div className="text-xs uppercase tracking-[0.12em] text-white/50">Label</div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <label className="col-span-1 flex flex-col gap-1">
                        <span className="text-white/60 text-xs">Icon</span>
                        <input
                          type="text"
                          maxLength={8}
                          value={mobileWidgetLabelIcon}
                          onChange={(event) => {
                            const value = event.target.value;
                            onWidgetCustomizationChange?.(mobileConfigWidget.id, { widgetLabelIcon: value || undefined });
                          }}
                          placeholder="✨"
                          className="rounded border border-white/16 bg-black/30 px-2 py-1.5 text-white"
                        />
                      </label>
                      <label className="col-span-2 flex flex-col gap-1">
                        <span className="text-white/60 text-xs">Name</span>
                        <input
                          type="text"
                          value={mobileWidgetLabelName}
                          onChange={(event) => {
                            const value = event.target.value;
                            onWidgetCustomizationChange?.(mobileConfigWidget.id, { title: value || undefined });
                          }}
                          placeholder={mobileConfigWidget.title}
                          className="rounded border border-white/16 bg-black/30 px-2 py-1.5 text-white"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Visibility */}
                  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-2">
                    <div className="text-xs uppercase tracking-[0.12em] text-white/50">Show In Row</div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      {(['short', 'medium', 'tall'] as const).map((row) => {
                        const checked = row === 'short' ? mobileConfigWidgetVisibility.showShort
                          : row === 'medium' ? mobileConfigWidgetVisibility.showMedium
                          : mobileConfigWidgetVisibility.showTall;
                        const label = row === 'short' ? 'Top' : row === 'medium' ? 'Middle' : 'Bottom';
                        return (
                          <label key={row} className="flex items-center gap-2 rounded-md border border-white/10 bg-black/20 px-2 py-2 cursor-pointer">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(c) => applyCycleWidgetVisibility(mobileConfigWidget.id, {
                                showInShort: row === 'short' ? c === true : mobileConfigWidgetVisibility.showShort,
                                showInMedium: row === 'medium' ? c === true : mobileConfigWidgetVisibility.showMedium,
                                showInTall: row === 'tall' ? c === true : mobileConfigWidgetVisibility.showTall,
                              })}
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sync Mode */}
                  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-2">
                    <div className="text-xs uppercase tracking-[0.12em] text-white/50">Sync Mode</div>
                    <div className={cn('grid gap-2 text-sm', showPrivilegedFixedSyncUi ? 'grid-cols-3' : 'grid-cols-2')}>
                      {(showPrivilegedFixedSyncUi
                        ? (['linked', 'isolated', 'fixed'] as Array<'linked' | 'isolated' | 'fixed'>)
                        : (['linked', 'isolated'] as Array<'linked' | 'isolated' | 'fixed'>)
                      ).map((mode) => {
                        const activeMode = widgetSyncModes?.[mobileConfigWidget.id] ?? widgetCustomizations?.[mobileConfigWidget.id]?.syncMode ?? 'linked';
                        const isActive = activeMode === mode;
                        return (
                          <button
                            key={`mobile-sync-${mobileConfigWidget.id}-${mode}`}
                            type="button"
                            onClick={() => {
                              if (onWidgetCustomizationChange) {
                                onWidgetCustomizationChange(mobileConfigWidget.id, {
                                  syncMode: mode,
                                  ...(mode === 'fixed' && !mobileWidgetFixedTimestamp
                                    ? { fixedTimestamp: toDateTimeLocalInputValue(currentDate) }
                                    : {}),
                                });
                              } else if (onToggleWidgetSync && !isActive) {
                                onToggleWidgetSync(mobileConfigWidget.id);
                              }
                            }}
                            className={cn(
                              'rounded-md border px-2 py-2 text-left capitalize transition-colors',
                              isActive
                                ? 'border-emerald-400/40 bg-emerald-500/20 text-emerald-100'
                                : 'border-white/16 bg-black/20 text-white/80 hover:bg-white/10'
                            )}
                          >
                            {mode}
                          </button>
                        );
                      })}
                    </div>
                    {showPrivilegedFixedSyncUi && (widgetSyncModes?.[mobileConfigWidget.id] ?? widgetCustomizations?.[mobileConfigWidget.id]?.syncMode ?? 'linked') === 'fixed' ? (
                      <label className="flex flex-col gap-1">
                        <span className="text-white/60 text-xs">Fixed Date & Time</span>
                        <input
                          type="datetime-local"
                          value={mobileWidgetFixedTimestamp}
                          onChange={(event) => {
                            const value = event.target.value;
                            onWidgetCustomizationChange?.(mobileConfigWidget.id, { fixedTimestamp: value || undefined });
                          }}
                          className="rounded border border-white/16 bg-black/30 px-2 py-1.5 text-white"
                        />
                      </label>
                    ) : null}
                  </div>

                  {/* Add Ring */}
                  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-2">
                    {mobileAddableRingPresets.length === 0 ? (
                      <div className="text-xs text-white/50 text-center py-1">All cycle rings already added.</div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowAddRingPicker((v) => !v)}
                          className="w-full rounded border border-emerald-300/35 bg-emerald-500/20 px-3 py-2 text-sm font-medium text-emerald-100 hover:bg-emerald-500/30 flex items-center justify-center gap-2"
                        >
                          <span className="text-base leading-none">{showAddRingPicker ? '✕' : '+'}</span>
                          <span>{showAddRingPicker ? 'Cancel' : 'Add Cycle Ring'}</span>
                        </button>
                        {showAddRingPicker && (
                          <div className="grid grid-cols-2 gap-1.5 pt-1">
                            {mobileAddableRingPresets.map((preset) => (
                              <button
                                key={`add-ring-pick-${preset.id}`}
                                type="button"
                                onClick={() => handleAddMobileCycleRingPreset(preset.id)}
                                className="rounded border border-white/16 bg-black/30 px-2 py-2 text-sm text-white hover:bg-emerald-500/20 hover:border-emerald-300/30 text-left"
                              >
                                {preset.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Ring list + properties */}
                  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-3">
                    <div className="text-xs uppercase tracking-[0.12em] text-white/50">Rings</div>
                    <div className="flex flex-wrap gap-1.5">
                      {cycleRings.map((ring) => (
                        <button
                          key={`ring-select-${ring.id}`}
                          type="button"
                          onClick={() => setMobileConfigRingId(ring.id)}
                          className={`px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
                            mobileConfigRingId === ring.id
                              ? 'border-emerald-300/50 bg-emerald-500/30 text-emerald-100'
                              : 'border-white/16 bg-black/30 text-white/80 hover:bg-white/10'
                          }`}
                        >
                          {ring.name}
                        </button>
                      ))}
                    </div>

                    {selectedMobileRing && (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <label className="flex flex-col gap-1 rounded-md border border-white/10 bg-black/20 px-2 py-2">
                          <span className="text-white/60 text-xs">Stages</span>
                          <input type="number" min={1} value={selectedMobileRing.stages}
                            onChange={(event) => patchMobileRing({ stages: Number(event.target.value) || 1 })}
                            className="rounded border border-white/16 bg-black/30 px-2 py-1 text-white" />
                        </label>
                        <div className="col-span-2 rounded-md border border-white/10 bg-black/20 px-2 py-2 space-y-2">
                          <span className="text-white/60 text-xs">Waveform</span>
                          <div className="flex flex-wrap gap-1.5">
                            {(['default', 'sine', 'triangle', 'square', 'sawtooth', '24hr'] as const).map((waveform) => (
                              <button
                                key={`waveform-${waveform}`}
                                type="button"
                                onClick={() => patchMobileRing({ waveform })}
                                className={cn(
                                  'rounded border px-2 py-1 text-xs capitalize',
                                  (selectedMobileRing.waveform ?? 'default') === waveform
                                    ? 'border-emerald-300/40 bg-emerald-500/20 text-emerald-100'
                                    : 'border-white/20 bg-black/30 text-white/80 hover:bg-white/10'
                                )}
                              >
                                {waveform}
                              </button>
                            ))}
                          </div>
                        </div>
                        <label className="flex flex-col gap-1 rounded-md border border-white/10 bg-black/20 px-2 py-2">
                          <span className="text-white/60 text-xs">Interval ×</span>
                          <input type="number" min={1} value={selectedMobileRing.waveformIntervalMultiplier ?? 1}
                            onChange={(event) => patchMobileRing({ waveformIntervalMultiplier: Number(event.target.value) || 1 })}
                            className="rounded border border-white/16 bg-black/30 px-2 py-1 text-white" />
                        </label>
                        <label className="flex flex-col gap-1 rounded-md border border-white/10 bg-black/20 px-2 py-2">
                          <span className="text-white/60 text-xs">Interval ÷</span>
                          <input type="number" min={1} value={selectedMobileRing.waveformIntervalDivider ?? 1}
                            onChange={(event) => patchMobileRing({ waveformIntervalDivider: Number(event.target.value) || 1 })}
                            className="rounded border border-white/16 bg-black/30 px-2 py-1 text-white" />
                        </label>
                        <div className="col-span-2 rounded-md border border-white/10 bg-black/20 px-2 py-2 space-y-2">
                          <span className="text-white/60 text-xs">Group Tag</span>
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() => patchMobileRing({ cycleGroup: undefined })}
                              className={cn(
                                'rounded border px-2 py-1 text-xs',
                                !(selectedMobileRing.cycleGroup ?? '')
                                  ? 'border-emerald-300/40 bg-emerald-500/20 text-emerald-100'
                                  : 'border-white/20 bg-black/30 text-white/80 hover:bg-white/10'
                              )}
                            >
                              None
                            </button>
                            {CYCLE_GROUP_PRESETS.map((preset) => (
                              <button
                                key={`mobile-ring-group-${preset.id}`}
                                type="button"
                                onClick={() => patchMobileRing({ cycleGroup: preset.id })}
                                className={cn(
                                  'rounded border px-2 py-1 text-xs',
                                  selectedMobileRing.cycleGroup === preset.id
                                    ? 'border-emerald-300/40 bg-emerald-500/20 text-emerald-100'
                                    : 'border-white/20 bg-black/30 text-white/80 hover:bg-white/10'
                                )}
                              >
                                {preset.name}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="col-span-2 rounded-md border border-white/10 bg-black/20 px-2 py-2 space-y-2">
                          <span className="text-white/60 text-xs">Parent Relation</span>
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() => patchMobileRing({ parentId: undefined })}
                              className={cn(
                                'rounded border px-2 py-1 text-xs',
                                !selectedMobileRing.parentId
                                  ? 'border-emerald-300/40 bg-emerald-500/20 text-emerald-100'
                                  : 'border-white/20 bg-black/30 text-white/80 hover:bg-white/10'
                              )}
                            >
                              None
                            </button>
                            <button
                              type="button"
                              onClick={() => patchMobileRing({ parentId: '__timeline__' })}
                              className={cn(
                                'rounded border px-2 py-1 text-xs',
                                selectedMobileRing.parentId === '__timeline__'
                                  ? 'border-emerald-300/40 bg-emerald-500/20 text-emerald-100'
                                  : 'border-white/20 bg-black/30 text-white/80 hover:bg-white/10'
                              )}
                            >
                              Timeline
                            </button>
                            {Object.values(PRESET_CYCLES)
                              .filter((p) => p.id !== selectedMobileRing.id)
                              .map((p) => (
                                <button
                                  key={`mobile-config-parent-${p.id}`}
                                  type="button"
                                  onClick={() => patchMobileRing({ parentId: p.id })}
                                  className={cn(
                                    'rounded border px-2 py-1 text-xs',
                                    selectedMobileRing.parentId === p.id
                                      ? 'border-emerald-300/40 bg-emerald-500/20 text-emerald-100'
                                      : 'border-white/20 bg-black/30 text-white/80 hover:bg-white/10'
                                  )}
                                >
                                  {p.name}
                                </button>
                              ))}
                          </div>
                        </div>
                        {selectedMobileRing.parentId ? (
                          <label className="flex flex-col gap-1 rounded-md border border-white/10 bg-black/20 px-2 py-2">
                            <span className="text-white/60 text-xs">Stages/Parent</span>
                            <input type="number" min={1} value={selectedMobileRing.stagesPerParent ?? 1}
                              onChange={(event) => patchMobileRing({ stagesPerParent: Number(event.target.value) || 1 })}
                              className="rounded border border-white/16 bg-black/30 px-2 py-1 text-white" />
                          </label>
                        ) : null}
                        <label className="flex items-center gap-2 rounded-md border border-white/10 bg-black/20 px-2 py-2 col-span-2 cursor-pointer">
                          <Checkbox
                            checked={selectedMobileRing.useCurrentStageAsValue === true}
                            onCheckedChange={(checked) => patchMobileRing({ useCurrentStageAsValue: checked === true })}
                          />
                          <span className="text-xs">Use current stage as value</span>
                        </label>

                        {/* Stage Icons */}
                        <div className="col-span-2 rounded-md border border-white/10 bg-black/20 px-2 py-2 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-white/60 text-xs">Stage Icons</span>
                            <button type="button" onClick={handleAiWidgetLabelSetup} disabled={!mobileConfigWidget}
                              className="rounded border border-violet-300/40 bg-violet-500/20 px-2 py-1 text-xs font-medium text-violet-100 hover:bg-violet-500/30 disabled:opacity-50">
                              AI Create
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {STAGE_ICON_PRESETS.map((preset) => (
                              <button key={`stage-icon-preset-${preset.id}`} type="button"
                                onClick={() => applyStageIconsToRing(preset.icons)}
                                className="rounded border border-white/20 bg-black/30 px-2 py-1 text-xs text-white/85 hover:bg-white/10">
                                {preset.name}
                              </button>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <input type="text" value={mobileStageIconsInput}
                              onChange={(event) => setMobileStageIconsInput(event.target.value)}
                              placeholder="🌑,🌒,🌓,🌔,🌕"
                              className="flex-1 rounded border border-white/16 bg-black/30 px-2 py-1 text-xs text-white" />
                            <button type="button"
                              onClick={() => {
                                const icons = mobileStageIconsInput.split(',').map((v) => v.trim()).filter((v) => v.length > 0);
                                if (icons.length > 0) applyStageIconsToRing(icons);
                              }}
                              className="rounded border border-white/20 bg-black/30 px-2 py-1 text-xs text-white/85 hover:bg-white/10">
                              Apply
                            </button>
                          </div>
                        </div>

                        {/* Stage Labels */}
                        <label className="col-span-2 flex flex-col gap-1 rounded-md border border-white/10 bg-black/20 px-2 py-2">
                          <span className="text-white/60 text-xs">Stage Labels (one per line)</span>
                          <textarea
                            value={mobileStageLabelsDraft}
                            onChange={(event) => setMobileStageLabelsDraft(event.target.value)}
                            rows={Math.min(6, Math.max(3, selectedMobileRing.stages))}
                            className="rounded border border-white/16 bg-black/30 px-2 py-1 text-xs text-white resize-none"
                            placeholder={"🌑 New Moon\n🌒 Crescent"}
                          />
                          <div className="flex justify-end">
                            <button type="button"
                              onClick={() => {
                                const lines = mobileStageLabelsDraft.split('\n').map((v) => v.trim()).filter((v) => v.length > 0);
                                if (lines.length > 0) patchMobileRing({ labels: lines });
                              }}
                              className="rounded border border-white/20 bg-black/30 px-2 py-1 text-xs text-white/85 hover:bg-white/10">
                              Save Labels
                            </button>
                          </div>
                        </label>
                      </div>
                    )}

                    {!selectedMobileRing && (
                      <div className="text-xs text-white/50 text-center py-3">Select a ring to edit.</div>
                    )}
                    </div>
                  </div>
                )}

                {!mobileConfigWidget && (
                  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-6 text-center text-sm text-white/50">
                    Select a widget above to configure it.
                  </div>
                )}

                {/* Bottom actions */}
                <div className="flex items-center justify-between gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => onSyncToNow?.()}
                    className="rounded-md border border-cyan-300/40 bg-cyan-500/20 px-3 py-2 text-sm font-medium text-cyan-100 hover:bg-cyan-500/30"
                  >
                    Sync To Now
                  </button>
                  <button type="button" onClick={() => { setShowMobileConfigDialog(false); setShowCycleLayoutDialog(true); }}
                    className="rounded-md border border-white/20 px-3 py-2 text-sm font-medium text-white/85 hover:bg-white/10">
                    Full Selector
                  </button>
                </div>
              </div>
          </DialogContent>
        </Dialog>

    </div>
  );
}

export default DreamspellSkin;
